import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import SessionWarning from '../components/SessionWarning';
import {
  Plus, Trash2, UserCheck, UserX, Edit2, X, Check, LogOut, ArrowLeft,
  Users, BookOpen, Upload, FileText, Video, File, Link as LinkIcon,
  CloudUpload, Folder, Wrench, Settings, Database, Globe, Shield,
  Truck, Hammer, Layers, Package, Box, Cpu, Compass, Map,
  Mountain, Snowflake, Sun, Wind, Camera, Image, Film, Music,
  Star, Heart, AlertCircle, Info, HelpCircle, Lock, Key, MessageSquare,
  Download, Search, ChevronDown, ChevronUp, Menu, Eye, Calendar, RefreshCw, ArrowRightLeft, DollarSign
} from 'lucide-react';
import AnalyticsWidget from '../components/AnalyticsWidget';
import ArticlesManager from '../components/ArticlesManager';
import VisitorMapWidget from '../components/VisitorMapWidget';
import WeeklyReportsSection from '../components/WeeklyReportsSection';
import OrderManagementTab from '../components/OrderManagementTab';
import CustomersTab from '../components/CustomersTab';
import ShareExportMenu from '../components/ShareExportMenu';
import GalleryManagementTab from '../components/GalleryManagementTab';
import FAQManagementTab from '../components/FAQManagementTab';
import EventsManagementTab from '../components/EventsManagementTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ['Technical Docs', 'Videos', 'Assembly Guides', 'Tutorials'];
const PRODUCTS = ['General', '2026 Truck Campers', 'Expedition Kits', 'Kei Truck Box', 'Truck Bodies', 'Custom Projects'];
const FILE_TYPES = ['link', 'video', 'pdf', 'doc', 'image'];

const FILE_ICONS = { pdf: FileText, video: Video, doc: File, link: LinkIcon, image: File };
const FILE_COLORS = { pdf: 'text-red-500', video: 'text-orange-500', doc: 'text-gray-400', link: 'text-green-500', image: 'text-gray-400' };

const FOLDER_ICONS = {
  Folder, Wrench, Settings, Database, Globe, Shield, Truck, Hammer,
  Layers, Package, Box, Cpu, Compass, Map, Mountain, Snowflake,
  Sun, Wind, Camera, Image, Film, Star, Heart, AlertCircle, Info,
  HelpCircle, Lock, Key, BookOpen, FileText, Video, Upload,
};

const PRESET_COLORS = [
  '#E8520A','#dc2626','#d97706','#16a34a','#2563eb','#7c3aed',
  '#db2777','#0891b2','#374151','#1a1a1a','#6b7280','#92400e',
];

const formatSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

/* ── Folder Visual Display ── */
const FolderVisual = ({ folder, size = 40 }) => {
  const token = localStorage.getItem('portal_token');
  const [imgSrc, setImgSrc] = useState('');
  useEffect(() => {
    if (folder.visual_type === 'image' && folder.cover_image) {
      fetch(`${BACKEND_URL}${folder.cover_image}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.blob()).then(b => setImgSrc(URL.createObjectURL(b)));
    }
  }, [folder.cover_image, folder.visual_type, token]);

  if (folder.visual_type === 'image' && imgSrc) {
    return <img src={imgSrc} alt={folder.name} style={{ width: size, height: size }} className="rounded-lg object-cover flex-shrink-0" />;
  }
  if (folder.visual_type === 'icon' && folder.icon && FOLDER_ICONS[folder.icon]) {
    const IconComp = FOLDER_ICONS[folder.icon];
    return (
      <div style={{ width: size, height: size, backgroundColor: folder.color || '#E8520A' }} className="rounded-lg flex items-center justify-center flex-shrink-0">
        <IconComp size={size * 0.5} className="text-white" />
      </div>
    );
  }
  return <div style={{ width: size, height: size, backgroundColor: folder.color || '#E8520A' }} className="rounded-lg flex-shrink-0" />;
};

/* ── Folder Visual Picker ── */
const FolderVisualPicker = ({ data, onChange, onCoverUpload, folderId }) => {
  const coverRef = useRef(null);
  const [activeTab, setActiveTab] = useState(data.visual_type || 'color');
  const inputCls = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none';

  const setTab = (t) => { setActiveTab(t); onChange({ ...data, visual_type: t }); };

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      {/* Tabs */}
      <div className="flex border-b border-gray-100 bg-gray-50">
        {[{ id: 'color', label: '🎨 Color' }, { id: 'icon', label: '⭐ Icon' }, { id: 'image', label: '🖼 Image' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`flex-1 py-2.5 text-xs font-bold transition-all ${activeTab === t.id ? 'bg-white text-orange-600 border-b-2 border-orange-400' : 'text-gray-500 hover:text-gray-700'}`}>
            {t.label}
          </button>
        ))}
      </div>

      <div className="p-4">
        {/* Color tab */}
        {activeTab === 'color' && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map(c => (
                <button key={c} onClick={() => onChange({ ...data, color: c, visual_type: 'color' })}
                  className={`w-8 h-8 rounded-lg border-2 transition-all hover:scale-110 ${data.color === c ? 'border-gray-700 scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 font-medium">Custom:</span>
              <input type="color" value={data.color || '#E8520A'}
                onChange={e => onChange({ ...data, color: e.target.value, visual_type: 'color' })}
                className="w-10 h-8 rounded cursor-pointer border border-gray-200" />
              <div className="w-8 h-8 rounded-lg flex-shrink-0" style={{ backgroundColor: data.color || '#E8520A' }} />
            </div>
          </div>
        )}

        {/* Icon tab */}
        {activeTab === 'icon' && (
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-xs text-gray-400 font-medium">Background:</span>
              <input type="color" value={data.color || '#E8520A'}
                onChange={e => onChange({ ...data, color: e.target.value })}
                className="w-8 h-8 rounded cursor-pointer border border-gray-200" />
            </div>
            <div className="grid grid-cols-8 gap-1.5 max-h-36 overflow-y-auto">
              {Object.entries(FOLDER_ICONS).map(([name, IconComp]) => (
                <button key={name} onClick={() => onChange({ ...data, icon: name, visual_type: 'icon' })}
                  title={name}
                  className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all hover:scale-110 ${data.icon === name ? 'ring-2 ring-orange-400' : ''}`}
                  style={{ backgroundColor: data.color || '#E8520A' }}>
                  <IconComp size={16} className="text-white" />
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Image tab */}
        {activeTab === 'image' && (
          <div>
            {folderId ? (
              <div className="space-y-3">
                <input ref={coverRef} type="file" accept="image/*" className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files[0]; if (!f) return;
                    await onCoverUpload(f);
                    onChange({ ...data, visual_type: 'image' });
                  }} />
                <div
                  onClick={() => coverRef.current?.click()}
                  className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-orange-300 transition-colors"
                >
                  {data.cover_image ? (
                    <p className="text-xs text-green-600 font-semibold">✓ Cover image uploaded. Click to replace.</p>
                  ) : (
                    <>
                      <Upload size={20} className="mx-auto mb-2 text-gray-300" />
                      <p className="text-xs text-gray-400">Click to upload a cover image (JPG, PNG, WebP — max 10 MB)</p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <p className="text-xs text-gray-400 py-4 text-center">Save the folder first, then you can upload a cover image.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── YouTube helpers ── */
const getYouTubeId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : null;
};
const getYouTubeThumbnail = (id) => `https://img.youtube.com/vi/${id}/mqdefault.jpg`;
const detectFileType = (url) => {
  if (!url) return 'link';
  if (getYouTubeId(url)) return 'video';
  if (/\.(mp4|mov|avi|mkv|webm)$/i.test(url)) return 'video';
  if (/\.(pdf)$/i.test(url)) return 'pdf';
  if (/\.(doc|docx|xls|xlsx|ppt|pptx)$/i.test(url)) return 'doc';
  if (/\.(jpg|jpeg|png|gif|webp|svg)$/i.test(url)) return 'image';
  return 'link';
};

/* ── Quick Add Link ── */
const QuickAddLink = ({ folders, users, onAdd }) => {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [category, setCategory] = useState('Technical Docs');
  const [folder_id, setFolderId] = useState('');
  const [access_level, setAccessLevel] = useState('general');
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const ytId = getYouTubeId(url);
  const detectedType = detectFileType(url);

  useEffect(() => {
    if (ytId) setPreview({ type: 'youtube', thumb: getYouTubeThumbnail(ytId), id: ytId });
    else if (url.startsWith('http')) setPreview({ type: 'link', url });
    else setPreview(null);
  }, [url, ytId]);

  const handleAdd = async () => {
    if (!url.trim() || !title.trim()) return;
    setLoading(true);
    await onAdd({ title, description: '', category, product: 'General', folder_id, url, file_type: detectedType, access_level, allowed_users: [], tags: [] });
    setUrl(''); setTitle(''); setPreview(null); setLoading(false);
  };

  const inputCls2 = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none bg-white';

  return (
    <div className="bg-white rounded-2xl border-2 border-dashed border-gray-200 p-6 hover:border-orange-300 transition-colors">
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FFF0E8' }}>
          <LinkIcon size={15} style={{ color: '#E8520A' }} />
        </div>
        <h3 className="font-black text-gray-900 text-sm uppercase tracking-widest">Quick Add Link or Video</h3>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* URL input + preview */}
        <div className="lg:col-span-2 space-y-3">
          <div className="relative">
            <input
              className={inputCls2 + ' pr-10'}
              placeholder="Paste any URL — YouTube, website, document link…"
              value={url}
              onChange={e => setUrl(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && title && handleAdd()}
            />
            {detectedType !== 'link' && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold px-2 py-0.5 rounded-full"
                style={{ backgroundColor: detectedType === 'video' ? '#FFF0E8' : '#EFF6FF', color: detectedType === 'video' ? '#E8520A' : '#2563eb' }}>
                {detectedType.toUpperCase()}
              </span>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <input className={inputCls2} placeholder="Title *" value={title} onChange={e => setTitle(e.target.value)} />
            <select className={inputCls2} value={category} onChange={e => setCategory(e.target.value)}>
              {CATEGORIES.map(c => <option key={c}>{c}</option>)}
            </select>
            <select className={inputCls2} value={folder_id} onChange={e => setFolderId(e.target.value)}>
              <option value="">— No folder —</option>
              {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <select className={inputCls2} value={access_level} onChange={e => setAccessLevel(e.target.value)}>
              <option value="general">🌐 General</option>
              <option value="admin">🔐 Admin Only</option>
            </select>
          </div>

          <button
            onClick={handleAdd}
            disabled={!url.trim() || !title.trim() || loading}
            className="px-6 py-2.5 font-bold text-white rounded-full text-sm transition-all hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#E8520A' }}
          >
            {loading ? 'ADDING…' : '+ ADD TO LIBRARY'}
          </button>
        </div>

        {/* Preview */}
        <div>
          {preview?.type === 'youtube' ? (
            <div className="rounded-xl overflow-hidden border border-gray-100 shadow-sm">
              <img src={preview.thumb} alt="YouTube thumbnail" className="w-full object-cover" />
              <div className="p-2 flex items-center gap-1.5">
                <Video size={12} style={{ color: '#E8520A' }} />
                <span className="text-xs text-gray-500 truncate">{title || 'YouTube Video'}</span>
              </div>
            </div>
          ) : preview?.type === 'link' ? (
            <div className="rounded-xl border border-gray-100 p-4 shadow-sm flex flex-col items-center justify-center text-center gap-2 h-full min-h-24">
              <Globe size={24} className="text-gray-300" />
              <p className="text-xs text-gray-400 break-all line-clamp-2">{url}</p>
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-600">LINK</span>
            </div>
          ) : (
            <div className="rounded-xl border border-dashed border-gray-100 p-4 flex flex-col items-center justify-center text-center gap-2 h-full min-h-24">
              <LinkIcon size={20} className="text-gray-200" />
              <p className="text-xs text-gray-300">Paste a URL to see a preview</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
const AccessSelector = ({ value, allowedUsers, onChange, onUsersChange, allUsers }) => {
  const nonAdmins = allUsers.filter(u => u.role !== 'admin');
  const toggleUser = (username) => {
    const next = allowedUsers.includes(username)
      ? allowedUsers.filter(u => u !== username)
      : [...allowedUsers, username];
    onUsersChange(next);
  };
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        {[
          { val: 'general', label: '🌐 General', desc: 'All portal users' },
          { val: 'admin', label: '🔐 Admin Only', desc: 'Admins only' },
          { val: 'specific', label: '👥 Specific Users', desc: 'Select below' },
        ].map(opt => (
          <button
            key={opt.val} type="button"
            onClick={() => onChange(opt.val)}
            className={`px-3 py-2 rounded-lg border text-left transition-all ${value === opt.val ? 'border-orange-400 bg-orange-50' : 'border-gray-200 bg-white hover:border-gray-300'}`}
          >
            <p className={`text-xs font-bold ${value === opt.val ? 'text-orange-600' : 'text-gray-700'}`}>{opt.label}</p>
            <p className="text-xs text-gray-400 mt-0.5">{opt.desc}</p>
          </button>
        ))}
      </div>
      {value === 'specific' && (
        <div className="border border-gray-200 rounded-lg p-3 bg-gray-50">
          {nonAdmins.length === 0 ? (
            <p className="text-xs text-gray-400">No non-admin users yet. Create users first.</p>
          ) : (
            <div className="space-y-1.5">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-2">Select users with access:</p>
              {nonAdmins.map(u => (
                <label key={u.id} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-all ${allowedUsers.includes(u.username) ? 'bg-orange-50 border border-orange-200' : 'bg-white border border-gray-100 hover:border-gray-200'}`}>
                  <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-all ${allowedUsers.includes(u.username) ? 'border-transparent' : 'border-gray-300'}`}
                    style={allowedUsers.includes(u.username) ? { backgroundColor: '#E8520A', borderColor: '#E8520A' } : {}}>
                    {allowedUsers.includes(u.username) && <Check size={9} className="text-white" />}
                  </div>
                  <input type="checkbox" className="sr-only" checked={allowedUsers.includes(u.username)} onChange={() => toggleUser(u.username)} />
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{u.username}</p>
                    <p className="text-xs text-gray-400">{u.email}</p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const AdminPanel = () => {
  const { user, logout, authHeaders, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [faqUnsaved, setFaqUnsaved] = useState(false);
  const [eventsUnsaved, setEventsUnsaved] = useState(false);

  const guardedSetTab = (newTab) => {
    if (faqUnsaved && tab === 'faq' && newTab !== 'faq') {
      if (!window.confirm('You have unsaved FAQ changes. Leave without saving?')) return;
      setFaqUnsaved(false);
    }
    if (eventsUnsaved && tab === 'events' && newTab !== 'events') {
      if (!window.confirm('You have unsaved Events changes. Leave without saving?')) return;
      setEventsUnsaved(false);
    }
    setTab(newTab);
  };
  
  // Get token from localStorage for components that need it
  const token = localStorage.getItem('portal_token');

  // Handle URL params for deep-linking (e.g. ?tab=orders&order=xxx from email)
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlTab = params.get('tab');
    if (urlTab) setTab(urlTab);
  }, []);

  // Chat History
  const [chatSessions, setChatSessions] = useState([]);
  const [chatLastUpdated, setChatLastUpdated] = useState(new Date());
  const [chatRefreshing, setChatRefreshing] = useState(false);
  const [expandedChat, setExpandedChat] = useState(null);
  const [chatSearch, setChatSearch] = useState('');
  const [selectedChats, setSelectedChats] = useState([]);
  const [chatSubTab, setChatSubTab] = useState('conversations');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [customerEmailFilter, setCustomerEmailFilter] = useState(null);
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filesSubTab, setFilesSubTab] = useState('folders');
  const [analyticsKey, setAnalyticsKey] = useState(0);
  const [analyticsRefreshing, setAnalyticsRefreshing] = useState(false);
  const [analyticsLastUpdated, setAnalyticsLastUpdated] = useState(new Date());

  const getSortedUsers = () => {
    let sorted = [...users];
    if (sortBy === 'name') {
      sorted.sort((a, b) => {
        const nameA = (a.username || '').toLowerCase();
        const nameB = (b.username || '').toLowerCase();
        return sortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
      });
    }
    return sorted;
  };

  const handleViewCustomerChats = (email, name) => {
    setCustomerEmailFilter(email);
    setChatSubTab('conversations');
    setChatSearch(email);
    flash(`Showing all chats from ${name}`);
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const getSortedChats = () => {
    let sorted = [...chatSessions];
    if (sortBy === 'name') {
      sorted.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return sortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
      });
    } else {
      sorted.sort((a, b) => {
        const dateA = new Date(a.last_updated || 0);
        const dateB = new Date(b.last_updated || 0);
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      });
    }
    return sorted;
  };

  const getSortedCustomers = () => {
    let sorted = [...customers];
    if (sortBy === 'name') {
      sorted.sort((a, b) => {
        const nameA = (a.name || '').toLowerCase();
        const nameB = (b.name || '').toLowerCase();
        return sortOrder === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
      });
    } else {
      sorted.sort((a, b) => {
        const dateA = new Date(a.last_session || 0);
        const dateB = new Date(b.last_session || 0);
        return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
      });
    }
    return sorted;
  };

  const toggleCustomerSelection = (email) => {
    setSelectedCustomers(prev => 
      prev.includes(email) 
        ? prev.filter(e => e !== email)
        : [...prev, email]
    );
  };

  const toggleAllCustomers = () => {
    setSelectedCustomers(
      selectedCustomers.length === customers.length 
        ? [] 
        : customers.map(c => c.email)
    );
  };

  const handleDeleteCustomers = async () => {
    if (selectedCustomers.length === 0) return;
    if (!window.confirm(`Delete all conversations from ${selectedCustomers.length} customer(s)? This will remove all their chat history. This cannot be undone.`)) return;
    
    try {
      // Get all session_ids for selected customers
      const sessionsToDelete = chatSessions
        .filter(s => selectedCustomers.includes(s.email))
        .map(s => s._id);
      
      if (sessionsToDelete.length > 0) {
        await axios.post(`${BACKEND_URL}/api/portal/admin/delete-chats`, 
          { session_ids: sessionsToDelete }, 
          { headers: authHeaders() }
        );
      }
      
      flash(`Deleted all conversations from ${selectedCustomers.length} customer(s)`);
      setSelectedCustomers([]);
      loadChatHistory();
      loadCustomers();
    } catch (error) {
      flash('Delete failed', true);
    }
  };
  
  // Profile editing
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileData, setProfileData] = useState({ username: '', email: '', password: '', newPassword: '' });
  const [currentTime, setCurrentTime] = useState(new Date());
  const [weeklyStats, setWeeklyStats] = useState(null);
  const [currencyRate, setCurrencyRate] = useState(0.73);
  const [editingRate, setEditingRate] = useState(false);
  const [rateInput, setRateInput] = useState('0.73');

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Fetch weekly stats for dashboard
  useEffect(() => {
    const fetchWeekly = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/portal/admin/weekly-stats`, { headers: authHeaders() });
        setWeeklyStats(res.data);
      } catch (e) { /* silent */ }
    };
    if (tab === 'dashboard') fetchWeekly();
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch currency rate
  useEffect(() => {
    if (tab === 'dashboard') {
      axios.get(`${BACKEND_URL}/api/settings/currency`)
        .then(r => { setCurrencyRate(r.data.cad_to_usd); setRateInput(String(r.data.cad_to_usd)); })
        .catch(() => {});
    }
  }, [tab]);

  const saveCurrencyRate = async () => {
    const rate = parseFloat(rateInput);
    if (isNaN(rate) || rate <= 0) { flash('Invalid rate', 'error'); return; }
    try {
      await axios.put(`${BACKEND_URL}/api/portal/admin/settings/currency`, { cad_to_usd: rate }, { headers: authHeaders() });
      setCurrencyRate(rate);
      setEditingRate(false);
      flash('Exchange rate updated');
    } catch { flash('Failed to update rate', 'error'); }
  };

  const handleEditProfile = () => {
    setProfileData({ 
      username: user?.username || '', 
      email: user?.email || '', 
      password: '',
      newPassword: '' 
    });
    setEditingProfile(true);
  };

  const handleSaveProfile = async () => {
    if (!profileData.password) {
      flash('Current password required to update profile', true);
      return;
    }
    
    try {
      const updateData = {
        username: profileData.username,
        email: profileData.email,
        currentPassword: profileData.password
      };
      if (profileData.newPassword) {
        updateData.newPassword = profileData.newPassword;
      }
      
      await axios.put(`${BACKEND_URL}/api/portal/profile`, updateData, { headers: authHeaders() });
      flash('Profile updated successfully');
      setEditingProfile(false);
      // Refresh user data
      window.location.reload();
    } catch (error) {
      flash(error.response?.data?.detail || 'Profile update failed', true);
    }
  };

  const toggleChatSelection = (sessionId) => {
    setSelectedChats(prev => 
      prev.includes(sessionId) 
        ? prev.filter(id => id !== sessionId)
        : [...prev, sessionId]
    );
  };

  const toggleAllChats = () => {
    const filtered = chatSessions.filter(s => 
      !chatSearch || 
      s.name?.toLowerCase().includes(chatSearch.toLowerCase()) ||
      s.email?.toLowerCase().includes(chatSearch.toLowerCase())
    );
    setSelectedChats(
      selectedChats.length === filtered.length 
        ? [] 
        : filtered.map(s => s._id)
    );
  };

  const handleDeleteSelectedChats = async () => {
    if (selectedChats.length === 0) return;
    if (!window.confirm(`Delete ${selectedChats.length} selected conversation(s)? This cannot be undone.`)) return;
    
    try {
      await axios.post(`${BACKEND_URL}/api/portal/admin/delete-chats`, { session_ids: selectedChats }, { headers: authHeaders() });
      flash(`Deleted ${selectedChats.length} conversation(s)`);
      setSelectedChats([]);
      setExpandedChat(null);
      loadChatHistory();
    } catch (error) {
      flash('Delete failed', true);
    }
  };

  // Users
  const [users, setUsers] = useState([]);
  const [showNewUser, setShowNewUser] = useState(false);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', role: 'user' });
  const [editingUser, setEditingUser] = useState(null);
  const [userGroups, setUserGroups] = useState([]);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [newGroup, setNewGroup] = useState({ name: '', description: '', parent_id: '', members: [] });
  const [editingGroup, setEditingGroup] = useState(null);
  const [bulkMigrate, setBulkMigrate] = useState({ show: false, fromGroupId: null, selectedUsers: [], toGroupId: '' });

  const startEditGroup = (group) => {
    setEditingGroup({ ...group });
  };

  const saveEditGroup = async () => {
    try {
      await axios.put(`${BACKEND_URL}/api/portal/admin/groups/${editingGroup.id}`, {
        name: editingGroup.name,
        description: editingGroup.description,
        parent_id: editingGroup.parent_id || null,
        members: editingGroup.members
      }, { headers: authHeaders() });
      flash('Group updated');
      setEditingGroup(null);
      loadGroups();
    } catch (error) {
      flash('Failed to update group', true);
    }
  };

  const addMemberToGroup = async (groupId, userId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/portal/admin/groups/${groupId}/members`, 
        { user_id: userId }, 
        { headers: authHeaders() }
      );
      flash('User added to group');
      loadGroups();
    } catch (error) {
      flash('Failed to add user', true);
    }
  };

  const removeMemberFromGroup = async (groupId, userId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/portal/admin/groups/${groupId}/members/${userId}`, 
        { headers: authHeaders() }
      );
      flash('User removed from group');
      loadGroups();
    } catch (error) {
      flash('Failed to remove user', true);
    }
  };

  // Folders
  const [folders, setFolders] = useState([]);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolder, setNewFolder] = useState({ name: '', description: '', color: '#E8520A', icon: '', cover_image: '', visual_type: 'color', access_level: 'general', allowed_users: [] });
  const [editingFolder, setEditingFolder] = useState(null);

  // Resources
  const [resources, setResources] = useState([]);
  const [showNewRes, setShowNewRes] = useState(false);
  const [newRes, setNewRes] = useState({ title: '', description: '', category: 'Technical Docs', product: 'General', folder_id: '', url: '', file_type: 'link', access_level: 'general', allowed_users: [] });
  const [editingRes, setEditingRes] = useState(null);

  // Upload
  const fileInputRef = useRef(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadMeta, setUploadMeta] = useState({ title: '', description: '', category: 'Technical Docs', product: 'General', access_level: 'general', allowed_users: [] });
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const flash = (m, isErr = false) => {
    if (isErr) setErr(m); else setMsg(m);
    setTimeout(() => { setMsg(''); setErr(''); }, 3000);
  };

  const loadUsers = useCallback(() => axios.get(`${BACKEND_URL}/api/portal/admin/users`, { headers: authHeaders() }).then(r => setUsers(r.data)).catch(() => {}), [authHeaders]);
  const loadResources = useCallback(() => axios.get(`${BACKEND_URL}/api/portal/resources`, { headers: authHeaders() }).then(r => setResources(r.data)).catch(() => {}), [authHeaders]);
  const loadFolders = useCallback(() => axios.get(`${BACKEND_URL}/api/portal/folders`, { headers: authHeaders() }).then(r => setFolders(r.data)).catch(() => {}), [authHeaders]);
  const loadChatHistory = useCallback(() => axios.get(`${BACKEND_URL}/api/portal/admin/chat-history`, { headers: authHeaders() }).then(r => { setChatSessions(r.data.sessions || []); setChatLastUpdated(new Date()); }).catch(() => {}), [authHeaders]);
  const loadCustomers = useCallback(() => axios.get(`${BACKEND_URL}/api/portal/admin/chat-customers`, { headers: authHeaders() }).then(r => { setCustomers(r.data.customers || []); setChatLastUpdated(new Date()); }).catch(() => {}), [authHeaders]);
  const loadGroups = useCallback(() => axios.get(`${BACKEND_URL}/api/portal/admin/groups`, { headers: authHeaders() }).then(r => setUserGroups(r.data || [])).catch(() => {}), [authHeaders]);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'admin') { navigate('/'); return; }
    loadUsers(); loadResources(); loadFolders(); loadChatHistory(); loadCustomers(); loadGroups();
  }, [user, authLoading, navigate, loadUsers, loadResources, loadFolders, loadChatHistory, loadCustomers, loadGroups]);

  const createGroup = async () => {
    if (!newGroup.name.trim()) { flash('Group name required', true); return; }
    try {
      const payload = { ...newGroup, parent_id: newGroup.parent_id || null };
      await axios.post(`${BACKEND_URL}/api/portal/admin/groups`, payload, { headers: authHeaders() });
      flash('Group created');
      setNewGroup({ name: '', description: '', parent_id: '', members: [] });
      setShowNewGroup(false);
      loadGroups();
    } catch (error) {
      flash('Failed to create group', true);
    }
  };

  const deleteGroup = async (groupId) => {
    const hasChildren = userGroups.some(g => g.parent_id === groupId);
    const msg = hasChildren
      ? 'Delete this group? Sub-groups will become top-level. Users will not be deleted.'
      : 'Delete this group? Users will not be deleted, only the group.';
    if (!window.confirm(msg)) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/portal/admin/groups/${groupId}`, { headers: authHeaders() });
      flash('Group deleted');
      loadGroups();
    } catch (error) {
      flash('Failed to delete group', true);
    }
  };

  const bulkMigrateMembers = async () => {
    if (!bulkMigrate.selectedUsers.length || !bulkMigrate.toGroupId) {
      flash('Select users and a target group', true); return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/portal/admin/groups/bulk-migrate`, {
        user_ids: bulkMigrate.selectedUsers,
        from_group_id: bulkMigrate.fromGroupId,
        to_group_id: bulkMigrate.toGroupId,
      }, { headers: authHeaders() });
      flash(`${bulkMigrate.selectedUsers.length} user(s) migrated`);
      setBulkMigrate({ show: false, fromGroupId: null, selectedUsers: [], toGroupId: '' });
      loadGroups();
    } catch (error) {
      flash('Failed to migrate users', true);
    }
  };

  const toggleBulkUser = (userId) => {
    setBulkMigrate(prev => ({
      ...prev,
      selectedUsers: prev.selectedUsers.includes(userId)
        ? prev.selectedUsers.filter(id => id !== userId)
        : [...prev.selectedUsers, userId]
    }));
  };

  // Build hierarchical group tree
  const getGroupTree = () => {
    const topLevel = userGroups.filter(g => !g.parent_id);
    const getChildren = (parentId) => userGroups.filter(g => g.parent_id === parentId);
    const buildTree = (groups, depth = 0) => {
      const result = [];
      for (const group of groups) {
        result.push({ ...group, depth });
        result.push(...buildTree(getChildren(group.id), depth + 1));
      }
      return result;
    };
    return buildTree(topLevel);
  };

  const getGroupPath = (groupId) => {
    const parts = [];
    let current = userGroups.find(g => g.id === groupId);
    while (current) {
      parts.unshift(current.name);
      current = current.parent_id ? userGroups.find(g => g.id === current.parent_id) : null;
    }
    return parts.join(' / ');
  };

  const handleExportCSV = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/portal/admin/chat-export-csv`, { 
        headers: authHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `disco_chat_contacts_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      flash('CSV downloaded successfully');
    } catch (error) {
      flash('Export failed', true);
    }
  };

  const exportChatPDF = () => {
    const filteredSessions = chatSessions.filter(s =>
      !chatSearch ||
      s.name?.toLowerCase().includes(chatSearch.toLowerCase()) ||
      s.email?.toLowerCase().includes(chatSearch.toLowerCase())
    );
    const data = selectedChats.length > 0
      ? filteredSessions.filter(s => selectedChats.includes(s._id))
      : filteredSessions;
    const fmtD = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
    const html = `<!DOCTYPE html><html><head><title>DISCO Chat History</title>
<style>
  body{font-family:Arial,sans-serif;margin:32px;color:#1a1a1a}
  h1{font-size:20px;margin-bottom:4px}
  .sub{font-size:12px;color:#888;margin-bottom:24px}
  .card{border:1px solid #e5e7eb;border-radius:12px;padding:16px;margin-bottom:12px;page-break-inside:avoid}
  .card-header{display:flex;align-items:center;justify-content:space-between;margin-bottom:10px}
  .name{font-weight:700;font-size:14px}
  .email{font-size:12px;color:#888}
  .meta{font-size:11px;color:#888}
  .msg{padding:8px 12px;margin:4px 0;border-radius:8px;font-size:13px}
  .msg-user{background:#fff7ed;border:1px solid #fed7aa;text-align:right}
  .msg-bot{background:#f9fafb;border:1px solid #e5e7eb}
  .msg-role{font-size:9px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px;margin-bottom:2px}
  @media print{body{margin:16px}.card{break-inside:avoid}}
</style></head><body>
<h1>DISCO Chat History</h1>
<p class="sub">Exported ${new Date().toLocaleString()} · ${data.length} conversations</p>
${data.map(s => `<div class="card">
  <div class="card-header">
    <div><span class="name">${s.name || 'Anonymous'}</span> <span class="email">${s.email || ''}</span></div>
    <span class="meta">${s.message_count || 0} messages · ${fmtD(s.last_updated)}</span>
  </div>
  ${(s.messages || []).map(m => `<div class="msg ${m.role === 'user' ? 'msg-user' : 'msg-bot'}">
    <div class="msg-role" style="color:${m.role === 'user' ? '#E8520A' : '#666'}">${m.role === 'user' ? s.name || 'Customer' : 'DISCO'}</div>
    ${m.content}
  </div>`).join('')}
</div>`).join('')}
</body></html>`;
    const sanitized = DOMPurify.sanitize(html, { WHOLE_DOCUMENT: true, ADD_TAGS: ['style', 'html', 'head', 'body', 'meta'], ADD_ATTR: ['charset'] });
    const blob = new Blob([sanitized], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (w) w.onload = () => { w.print(); URL.revokeObjectURL(url); };
  };


  // Folder actions
  const createFolder = async () => {
    if (!newFolder.name.trim()) { flash('Folder name required', true); return; }
    try {
      await axios.post(`${BACKEND_URL}/api/portal/folders`, newFolder, { headers: authHeaders() });
      flash('Folder created');
      setNewFolder({ name: '', description: '', color: '#E8520A', icon: '', cover_image: '', visual_type: 'color', access_level: 'general', allowed_users: [] });
      setShowNewFolder(false); loadFolders();
    } catch (e) { flash(e.response?.data?.detail || 'Error', true); }
  };

  const saveEditFolder = async () => {
    try {
      const { id, created_at, active, ...fields } = editingFolder;
      await axios.put(`${BACKEND_URL}/api/portal/folders/${id}`, fields, { headers: authHeaders() });
      flash('Folder updated'); setEditingFolder(null); loadFolders();
    } catch (e) { flash('Error', true); }
  };

  const deleteFolder = async (id) => {
    if (!window.confirm('Delete folder? Resources inside will be moved to root.')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/portal/folders/${id}`, { headers: authHeaders() });
      flash('Folder deleted'); loadFolders(); loadResources();
    } catch (e) { flash('Error', true); }
  };

  const uploadFolderCover = async (folderId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/portal/folders/${folderId}/cover`, formData, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      flash('Cover image uploaded');
      if (editingFolder?.id === folderId) setEditingFolder(p => ({ ...p, cover_image: res.data.url, visual_type: 'image' }));
      loadFolders();
    } catch (e) { flash('Error uploading cover', true); }
  };

  // File upload handler
  const handleUpload = async () => {
    if (!uploadFile || !uploadMeta.title.trim()) { flash('Please select a file and enter a title', true); return; }
    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('title', uploadMeta.title);
    formData.append('description', uploadMeta.description);
    formData.append('category', uploadMeta.category);
    formData.append('access_level', uploadMeta.access_level);
    formData.append('allowed_users', uploadMeta.allowed_users.join(','));
    formData.append('folder_id', uploadMeta.folder_id || '');
    setUploading(true); setUploadProgress(0);
    try {
      await axios.post(`${BACKEND_URL}/api/portal/upload`, formData, {
        headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadProgress(Math.round((e.loaded * 100) / e.total)),
      });
      flash('File uploaded successfully');
      setUploadFile(null); setUploadProgress(0);
      setUploadMeta({ title: '', description: '', category: 'Technical Docs', product: 'General', access_level: 'general', allowed_users: [] });
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadResources();
    } catch (e) { flash(e.response?.data?.detail || 'Upload failed', true); }
    finally { setUploading(false); }
  };

  const handleDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) { setUploadFile(f); if (!uploadMeta.title) setUploadMeta(p => ({ ...p, title: f.name.replace(/\.[^.]+$/, '') })); }
  };

  // User actions
  const createUser = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/portal/admin/users`, newUser, { headers: authHeaders() });
      flash('User created successfully');
      setNewUser({ username: '', email: '', password: '', role: 'user' });
      setShowNewUser(false);
      loadUsers();
    } catch (e) { flash(e.response?.data?.detail || 'Error creating user', true); }
  };

  const toggleUser = async (u) => {
    try {
      await axios.put(`${BACKEND_URL}/api/portal/admin/users/${u.id}`, { active: !u.active }, { headers: authHeaders() });
      flash(`User ${u.active ? 'disabled' : 'enabled'}`);
      loadUsers();
    } catch (e) { flash('Error updating user', true); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/portal/admin/users/${id}`, { headers: authHeaders() });
      flash('User deleted'); loadUsers();
    } catch (e) { flash('Error deleting user', true); }
  };

  const saveEditUser = async () => {
    try {
      const payload = { username: editingUser.username, email: editingUser.email };
      if (editingUser.newPassword?.trim()) payload.password = editingUser.newPassword;
      await axios.put(`${BACKEND_URL}/api/portal/admin/users/${editingUser.id}`, payload, { headers: authHeaders() });
      flash('User updated'); setEditingUser(null); loadUsers();
    } catch (e) { flash('Error updating user', true); }
  };

  // Resource actions
  const createResource = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/portal/resources`, newRes, { headers: authHeaders() });
      flash('Resource added');
      setNewRes({ title: '', description: '', category: 'Technical Docs', product: 'General', url: '', file_type: 'link' });
      setShowNewRes(false); loadResources();
    } catch (e) { flash('Error adding resource', true); }
  };

  const deleteResource = async (id) => {
    if (!window.confirm('Remove this resource?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/portal/resources/${id}`, { headers: authHeaders() });
      flash('Resource removed'); loadResources();
    } catch (e) { flash('Error', true); }
  };

  const saveEditRes = async () => {
    try {
      const { id, created_at, active, ...fields } = editingRes;
      await axios.put(`${BACKEND_URL}/api/portal/resources/${id}`, fields, { headers: authHeaders() });
      flash('Resource updated'); setEditingRes(null); loadResources();
    } catch (e) { flash('Error', true); }
  };

  const inputCls = "w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 bg-white focus:outline-none";

  if (authLoading) {
    return (
      <div className="min-h-screen pt-[76px] bg-gray-50 flex items-center justify-center">
        <div className="text-gray-400 text-sm">Loading...</div>
      </div>
    );
  }

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="min-h-screen pt-[76px] bg-gray-50">
      <SessionWarning />
      {/* Header */}
      <div className="bg-zinc-900 py-4 sm:py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <button onClick={() => navigate('/portal')} className="text-gray-500 hover:text-white transition-colors">
              <ArrowLeft size={18} />
            </button>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#E8520A' }}>Admin Panel</p>
              <h1 className="text-lg sm:text-2xl font-black text-white uppercase" style={{ fontFamily: 'Inter, sans-serif' }}>Portal Management</h1>
            </div>
          </div>
          <button onClick={() => { logout(); navigate('/'); }} className="flex items-center gap-2 px-3 sm:px-4 py-2 text-xs font-bold text-gray-400 border border-gray-700 rounded-full hover:text-white transition-all">
            <LogOut size={13} /> <span className="hidden sm:inline">Sign Out</span>
          </button>
        </div>
      </div>

      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {/* Alerts */}
        {msg && <div className="mb-4 bg-green-50 border border-green-200 rounded-xl px-4 py-3 text-sm text-green-700 flex items-center gap-2"><Check size={14} /> {msg}</div>}
        {err && <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">{err}</div>}

        {/* ── Mobile Menu Bar (inside content, not navbar) ── */}
        <div
          onClick={() => setSidebarOpen(true)}
          className="lg:hidden flex items-center gap-3 bg-white border border-gray-200 rounded-xl px-4 py-3 mb-4 cursor-pointer hover:border-gray-300 transition-all w-full"
          data-testid="mobile-menu-bar"
        >
          <Menu size={18} className="text-gray-500" />
          <div className="flex-1 min-w-0">
            <span className="text-sm font-bold text-gray-800">
              {(() => {
                const labels = { dashboard: 'Dashboard', users: 'Users & Access', files: 'Files & Resources', orders: 'Orders & Contacts', chats: 'Chat History', customers: 'Customers', gallery: 'Gallery', analytics: 'Web Analytics' };
                return labels[tab] || 'Dashboard';
              })()}
            </span>
            <span className="text-[10px] text-gray-400 ml-2">
              {(() => {
                const groups = { dashboard: '', users: 'Partners Portal', files: 'Partners Portal', orders: 'Orders & Activity', chats: 'Orders & Activity', customers: 'Orders & Activity', gallery: 'Website', analytics: 'Website' };
                return groups[tab] || '';
              })()}
            </span>
          </div>
          <ChevronDown size={14} className="text-gray-400" />
        </div>

        <div className="flex gap-6">
          {/* Sidebar */}
          <div className={`${sidebarOpen ? 'fixed inset-0 z-30 bg-black/40 lg:bg-transparent lg:static lg:inset-auto' : 'hidden lg:block'}`} onClick={() => setSidebarOpen(false)}>
            <nav
              className={`bg-white border border-gray-200 rounded-xl w-56 flex-shrink-0 overflow-hidden ${
                sidebarOpen ? 'fixed left-4 top-4 bottom-4 z-40 shadow-2xl overflow-y-auto' : ''
              }`}
              onClick={e => e.stopPropagation()}
              data-testid="admin-sidebar"
            >
              <div className="p-3">
                {/* Dashboard — no sub-menu */}
                <button
                  onClick={() => { guardedSetTab('dashboard'); setSidebarOpen(false); }}
                  className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-bold transition-all mb-1 ${
                    tab === 'dashboard' ? 'text-white' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                  style={tab === 'dashboard' ? { backgroundColor: '#E8520A' } : {}}
                  data-testid="nav-dashboard"
                >
                  <Compass size={15} /> Dashboard
                </button>

                {/* Partners Portal */}
                <div className="mt-3 mb-1">
                  <p className="px-3 text-[9px] font-black uppercase tracking-[0.15em] text-gray-400 mb-1.5">Partners Portal</p>
                  {[
                    { id: 'users', label: 'Users & Access', Icon: Users },
                    { id: 'files', label: 'Files & Resources', Icon: Folder },
                  ].map(({ id, label, Icon }) => (
                    <button
                      key={id}
                      onClick={() => { guardedSetTab(id); if (id === 'files') setFilesSubTab('folders'); setSidebarOpen(false); }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-semibold transition-all mb-0.5 ${
                        tab === id ? 'text-white' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                      style={tab === id ? { backgroundColor: '#E8520A' } : {}}
                      data-testid={`nav-${id}`}
                    >
                      <Icon size={14} /> {label}
                    </button>
                  ))}
                </div>

                {/* Orders & Activity */}
                <div className="mt-3 mb-1">
                  <p className="px-3 text-[9px] font-black uppercase tracking-[0.15em] text-gray-400 mb-1.5">Orders & Activity</p>
                  {[
                    { id: 'orders', label: 'Orders & Contacts', Icon: Package },
                    { id: 'chats', label: 'Chat History', Icon: MessageSquare },
                    { id: 'customers', label: 'Customers', Icon: Users },
                  ].map(({ id, label, Icon }) => (
                    <button
                      key={id}
                      onClick={() => { guardedSetTab(id); setSidebarOpen(false); }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-semibold transition-all mb-0.5 ${
                        tab === id ? 'text-white' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                      style={tab === id ? { backgroundColor: '#E8520A' } : {}}
                      data-testid={`nav-${id}`}
                    >
                      <Icon size={14} /> {label}
                    </button>
                  ))}
                </div>

                {/* Website */}
                <div className="mt-3 mb-1">
                  <p className="px-3 text-[9px] font-black uppercase tracking-[0.15em] text-gray-400 mb-1.5">Website</p>
                  {[
                    { id: 'gallery', label: 'Gallery', Icon: Image },
                    { id: 'events', label: 'Events', Icon: Calendar },
                    { id: 'articles', label: 'Articles', Icon: BookOpen },
                    { id: 'faq', label: 'FAQ', Icon: HelpCircle },
                    { id: 'analytics', label: 'Web Analytics', Icon: Globe },
                  ].map(({ id, label, Icon }) => (
                    <button
                      key={id}
                      onClick={() => { guardedSetTab(id); setSidebarOpen(false); }}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-semibold transition-all mb-0.5 ${
                        tab === id ? 'text-white' : 'text-gray-600 hover:bg-gray-50'
                      }`}
                      style={tab === id ? { backgroundColor: '#E8520A' } : {}}
                      data-testid={`nav-${id}`}
                    >
                      <Icon size={14} /> {label}
                    </button>
                  ))}
                </div>
              </div>
            </nav>
          </div>

          {/* ── Main Content Area ── */}
          <div className="flex-1 min-w-0">

        {/* ── USERS TAB ── */}

        {/* ── DASHBOARD TAB ── */}
        {tab === 'dashboard' && (
          <div className="p-4 sm:p-8">
            {/* Welcome Message with Live Time */}
            <div className="mb-6 sm:mb-8 pb-4 sm:pb-6 border-b border-gray-200">
              <h1 className="text-xl sm:text-3xl font-black text-gray-900 mb-2">


                Welcome back, {user?.username || 'Admin'}!
              </h1>
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 text-xs sm:text-sm text-gray-500">
                <span className="flex items-center gap-2">
                  📅 {currentTime.toLocaleDateString('en-US', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </span>
                <span className="hidden sm:inline">•</span>
                <span className="flex items-center gap-2">
                  🕐 {currentTime.toLocaleTimeString('en-US', { 
                    hour: '2-digit', 
                    minute: '2-digit',
                    second: '2-digit'
                  })}
                </span>
              </div>
            </div>
            
            <h2 className="text-xl sm:text-2xl font-black text-gray-900">Dashboard Overview</h2>
            <p className="text-sm text-gray-500 mt-1 mb-4 sm:mb-6">Weekly performance and account settings</p>
            
            {/* Weekly Review */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 mb-6 sm:mb-8" data-testid="weekly-review">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 sm:mb-5">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FFF0E8' }}>
                    <Calendar size={16} style={{ color: '#E8520A' }} />
                  </div>
                  <div>
                    <h3 className="text-sm sm:text-base font-black text-gray-900">Weekly Review</h3>
                    <p className="text-[10px] sm:text-xs text-gray-400">Week starting Monday</p>
                  </div>
                </div>
                {weeklyStats?.period && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 rounded-lg text-xs font-bold text-gray-600" data-testid="weekly-period-label">
                    <Calendar size={12} className="text-gray-400" />
                    {weeklyStats.period.label}
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4" data-testid="weekly-stat-cards">
                {[
                  { key: 'visits', label: 'Visits / Sessions', icon: Eye, color: '#E8520A' },
                  { key: 'orders', label: 'Orders', icon: Package, color: '#2563eb' },
                  { key: 'chats', label: 'Chat Sessions', icon: MessageSquare, color: '#7c3aed' },
                  { key: 'forms', label: 'Contact Forms', icon: FileText, color: '#16a34a' },
                ].map(({ key, label, icon: Icon, color }) => {
                  const curr = weeklyStats?.current?.[key] ?? 0;
                  const change = weeklyStats?.changes?.[key] ?? 0;
                  const isUp = change >= 0;
                  return (
                    <div key={key} className="bg-gray-50 border border-gray-100 rounded-xl p-3 sm:p-4 hover:shadow-sm transition-shadow" data-testid={`weekly-stat-${key}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] sm:text-xs font-bold uppercase tracking-wider text-gray-400">{label}</span>
                        <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                          <Icon size={14} style={{ color }} />
                        </div>
                      </div>
                      <p className="text-xl sm:text-2xl font-black text-gray-900">{curr}</p>
                      <div className="flex items-center gap-1 mt-1">
                        {isUp ? (
                          <ChevronUp size={12} className="text-green-500" />
                        ) : (
                          <ChevronDown size={12} className="text-red-500" />
                        )}
                        <span className={`text-[10px] sm:text-xs font-bold ${isUp ? 'text-green-600' : 'text-red-600'}`}>
                          {isUp ? '+' : ''}{change}%
                        </span>
                        <span className="text-[10px] text-gray-400 ml-0.5">vs last week</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Account Management */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-black text-gray-900">Account Information</h3>
                {!editingProfile && (
                  <button
                    onClick={handleEditProfile}
                    className="inline-flex items-center gap-2 px-4 py-2 text-sm font-bold text-gray-700 border border-gray-300 rounded-lg hover:border-orange-500 hover:text-orange-600 transition-all"
                  >
                    <Edit2 size={14} /> Edit Profile
                  </button>
                )}
              </div>
              
              {!editingProfile ? (
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center">
                    <span className="text-2xl font-black text-orange-600">{user?.username?.charAt(0) || 'A'}</span>
                  </div>
                  <div>
                    <p className="text-base font-bold text-gray-900">{user?.username || 'Admin'}</p>
                    <p className="text-sm text-gray-500">{user?.email || 'admin@totalcomposites.com'}</p>
                    <span className="inline-block mt-1 px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-bold rounded">
                      ADMIN
                    </span>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Username</label>
                      <input
                        type="text"
                        value={profileData.username}
                        onChange={(e) => setProfileData(p => ({ ...p, username: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Email</label>
                      <input
                        type="email"
                        value={profileData.email}
                        onChange={(e) => setProfileData(p => ({ ...p, email: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-orange-500"
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">Current Password *</label>
                      <input
                        type="password"
                        value={profileData.password}
                        onChange={(e) => setProfileData(p => ({ ...p, password: e.target.value }))}
                        placeholder="Required to save changes"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-orange-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">New Password (Optional)</label>
                      <input
                        type="password"
                        value={profileData.newPassword}
                        onChange={(e) => setProfileData(p => ({ ...p, newPassword: e.target.value }))}
                        placeholder="Leave blank to keep current"
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-orange-500"
                      />
                    </div>
                  </div>
                  
                  <div className="flex gap-3">
                    <button
                      onClick={handleSaveProfile}
                      className="px-6 py-2.5 font-bold text-white rounded-lg text-sm transition-all hover:opacity-90"
                      style={{ backgroundColor: '#E8520A' }}
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => setEditingProfile(false)}
                      className="px-6 py-2.5 font-bold text-gray-700 border border-gray-300 rounded-lg text-sm hover:border-gray-400 transition-all"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Currency Exchange Rate */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 mb-6" data-testid="currency-rate-admin">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FFF3ED' }}>
                    <DollarSign size={18} style={{ color: '#E8520A' }} />
                  </div>
                  <div>
                    <h3 className="text-sm font-black text-gray-900">Currency Exchange Rate</h3>
                    <p className="text-xs text-gray-500">CAD to USD conversion for pricing toggle</p>
                  </div>
                </div>
                {!editingRate ? (
                  <button onClick={() => setEditingRate(true)} className="text-xs font-bold text-gray-500 hover:text-orange-600 transition-colors flex items-center gap-1">
                    <Edit2 size={12} /> Edit
                  </button>
                ) : (
                  <div className="flex gap-2">
                    <button onClick={saveCurrencyRate} className="text-xs font-bold text-green-600 hover:text-green-700 flex items-center gap-1"><Check size={12} /> Save</button>
                    <button onClick={() => { setEditingRate(false); setRateInput(String(currencyRate)); }} className="text-xs font-bold text-gray-400 hover:text-gray-600 flex items-center gap-1"><X size={12} /> Cancel</button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-4">
                <div className="flex-1 flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: '#FAFAFA', border: '1px solid #eee' }}>
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">1 CAD =</span>
                  {editingRate ? (
                    <input
                      type="number" step="0.0001" min="0.01" value={rateInput}
                      onChange={e => setRateInput(e.target.value)}
                      className="w-24 px-2 py-1 border border-gray-300 rounded text-sm font-bold text-gray-900 bg-white"
                      data-testid="currency-rate-input"
                    />
                  ) : (
                    <span className="text-lg font-black text-gray-900">{currencyRate}</span>
                  )}
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-widest">USD</span>
                </div>
                <div className="text-xs text-gray-400 leading-relaxed hidden sm:block">
                  <p>Example: $10,000 CAD</p>
                  <p className="font-bold text-gray-600">= ${(10000 * currencyRate).toLocaleString('en-US', { minimumFractionDigits: 2 })} USD</p>
                </div>
              </div>
            </div>

            {/* Website Analytics */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 mb-6">
              <AnalyticsWidget />
            </div>

            
            {/* Quick Actions */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6">
              <h3 className="text-lg font-black text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <button
                  onClick={() => guardedSetTab('users')}
                  className="flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all text-left"
                >
                  <Users size={20} className="text-gray-600" />
                  <div>
                    <p className="text-sm font-bold text-gray-900">Manage Users</p>
                    <p className="text-xs text-gray-500">{users.length} users</p>
                  </div>
                </button>
                
                <button
                  onClick={() => guardedSetTab('files')}
                  className="flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all text-left"
                >
                  <Folder size={20} className="text-gray-600" />
                  <div>
                    <p className="text-sm font-bold text-gray-900">Manage Files</p>
                    <p className="text-xs text-gray-500">{resources.length} resources</p>
                  </div>
                </button>
                
                <button
                  onClick={() => guardedSetTab('chats')}
                  className="flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all text-left"
                >
                  <MessageSquare size={20} className="text-gray-600" />
                  <div>
                    <p className="text-sm font-bold text-gray-900">View Chats</p>
                    <p className="text-xs text-gray-500">{chatSessions.length} conversations</p>
                  </div>
                </button>
                
                <button
                  onClick={handleExportCSV}
                  className="flex items-center gap-3 px-4 py-3 border border-gray-200 rounded-lg hover:border-orange-500 hover:bg-orange-50 transition-all text-left"
                >
                  <Download size={20} className="text-gray-600" />
                  <div>
                    <p className="text-sm font-bold text-gray-900">Export Contacts</p>
                    <p className="text-xs text-gray-500">Download CSV</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── ORDERS TAB ── */}
        {tab === 'orders' && (
          <OrderManagementTab authHeaders={authHeaders} flash={flash} />
        )}

        {/* ── CUSTOMERS TAB ── */}
        {tab === 'customers' && (
          <CustomersTab authHeaders={authHeaders} flash={flash} />
        )}

        {tab === 'users' && (
          <div className="p-4 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <div>
                <h2 className="text-xl sm:text-2xl font-black text-gray-900">Portal Users</h2>
                <p className="text-sm text-gray-500 mt-1">{users.length} registered users</p>
              </div>
              <button onClick={() => setShowNewUser(true)} className="inline-flex items-center gap-2 px-5 py-2 font-bold text-white rounded-full text-sm self-start sm:self-auto" style={{ backgroundColor: '#E8520A' }}>
                <Plus size={14} /> ADD USER
              </button>
            </div>

            {/* New User Form */}
            {showNewUser && (
              <div className="bg-white rounded-2xl border border-orange-200 p-4 sm:p-6 mb-5 shadow-sm">
                <h3 className="font-black text-gray-900 mb-4 text-sm uppercase tracking-widest">New User</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                  <input className={inputCls} placeholder="Username *" value={newUser.username} onChange={e => setNewUser(p => ({ ...p, username: e.target.value }))} />
                  <input className={inputCls} placeholder="Email *" type="email" value={newUser.email} onChange={e => setNewUser(p => ({ ...p, email: e.target.value }))} />
                  <input className={inputCls} placeholder="Password *" type="password" value={newUser.password} onChange={e => setNewUser(p => ({ ...p, password: e.target.value }))} />
                  <select className={inputCls + ' bg-white'} value={newUser.role} onChange={e => setNewUser(p => ({ ...p, role: e.target.value }))}>
                    <option value="user">User</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="flex gap-2">
                  <button onClick={createUser} className="px-5 py-2 font-bold text-white rounded-full text-sm" style={{ backgroundColor: '#E8520A' }}>CREATE</button>
                  <button onClick={() => setShowNewUser(false)} className="px-5 py-2 font-bold text-gray-600 rounded-full text-sm border border-gray-200">CANCEL</button>
                </div>
              </div>
            )}


            {/* User Groups Section */}
            <div className="mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                <h3 className="text-lg font-black text-gray-900" data-testid="groups-heading">User Groups</h3>
                <button 
                  onClick={() => setShowNewGroup(true)} 
                  className="inline-flex items-center gap-2 px-4 py-2 font-bold text-sm border border-gray-300 text-gray-700 rounded-lg hover:border-orange-500 hover:text-orange-600 transition-all"
                  data-testid="create-group-btn"
                >
                  <Plus size={14} /> CREATE GROUP
                </button>
              </div>
              
              {showNewGroup && (
                <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4" data-testid="new-group-form">
                  <h4 className="text-sm font-bold text-gray-900 mb-3">New Group</h4>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-3">
                    <input
                      className={inputCls}
                      placeholder="Group name *"
                      value={newGroup.name}
                      onChange={(e) => setNewGroup(p => ({ ...p, name: e.target.value }))}
                      data-testid="new-group-name"
                    />
                    <input
                      className={inputCls}
                      placeholder="Description"
                      value={newGroup.description}
                      onChange={(e) => setNewGroup(p => ({ ...p, description: e.target.value }))}
                      data-testid="new-group-desc"
                    />
                    <select
                      className={inputCls}
                      value={newGroup.parent_id}
                      onChange={(e) => setNewGroup(p => ({ ...p, parent_id: e.target.value }))}
                      data-testid="new-group-parent"
                    >
                      <option value="">No parent (top-level)</option>
                      {userGroups.map(g => (
                        <option key={g.id} value={g.id}>{getGroupPath(g.id)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={createGroup} className="px-4 py-2 font-bold text-white rounded-lg text-sm" style={{ backgroundColor: '#E8520A' }} data-testid="submit-new-group">
                      CREATE
                    </button>
                    <button onClick={() => { setShowNewGroup(false); setNewGroup({ name: '', description: '', parent_id: '', members: [] }); }} className="px-4 py-2 font-bold text-gray-600 border border-gray-300 rounded-lg text-sm">
                      CANCEL
                    </button>
                  </div>
                </div>
              )}

              {/* Bulk Migration Panel */}
              {bulkMigrate.show && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4" data-testid="bulk-migrate-panel">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-bold text-gray-900">
                      Bulk Migrate from: <span style={{ color: '#E8520A' }}>{userGroups.find(g => g.id === bulkMigrate.fromGroupId)?.name}</span>
                    </h4>
                    <button onClick={() => setBulkMigrate({ show: false, fromGroupId: null, selectedUsers: [], toGroupId: '' })} className="text-gray-400 hover:text-gray-600">
                      <X size={16} />
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">Select users to move, then choose a destination group.</p>
                  <div className="flex flex-wrap gap-2 mb-3 max-h-32 overflow-y-auto">
                    {(userGroups.find(g => g.id === bulkMigrate.fromGroupId)?.members || []).map(userId => {
                      const u = users.find(x => x.id === userId);
                      if (!u) return null;
                      const selected = bulkMigrate.selectedUsers.includes(userId);
                      return (
                        <button
                          key={userId}
                          onClick={() => toggleBulkUser(userId)}
                          className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold rounded-full border transition-all ${
                            selected ? 'border-orange-500 bg-orange-100 text-orange-700' : 'border-gray-200 bg-white text-gray-600 hover:border-gray-400'
                          }`}
                          data-testid={`bulk-user-${userId}`}
                        >
                          {selected && <Check size={10} />}
                          {u.username}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex items-center gap-3">
                    <select
                      className={inputCls + ' flex-1'}
                      value={bulkMigrate.toGroupId}
                      onChange={(e) => setBulkMigrate(p => ({ ...p, toGroupId: e.target.value }))}
                      data-testid="bulk-target-group"
                    >
                      <option value="">Move to group...</option>
                      {userGroups
                        .filter(g => g.id !== bulkMigrate.fromGroupId)
                        .map(g => (
                          <option key={g.id} value={g.id}>{getGroupPath(g.id)}</option>
                        ))}
                    </select>
                    <button
                      onClick={bulkMigrateMembers}
                      disabled={!bulkMigrate.selectedUsers.length || !bulkMigrate.toGroupId}
                      className="px-4 py-2 font-bold text-white rounded-lg text-sm disabled:opacity-40 transition-all"
                      style={{ backgroundColor: '#E8520A' }}
                      data-testid="bulk-migrate-submit"
                    >
                      MIGRATE ({bulkMigrate.selectedUsers.length})
                    </button>
                  </div>
                </div>
              )}
              
              {/* Group Cards — hierarchical */}
              <div className="space-y-2" data-testid="groups-list">
                {getGroupTree().map(group => (
                  <div
                    key={group.id}
                    className="bg-white border border-gray-200 rounded-lg p-4 transition-all hover:border-gray-300"
                    style={{ marginLeft: group.depth * 32 }}
                    data-testid={`group-card-${group.id}`}
                  >
                    {editingGroup?.id === group.id ? (
                      /* Edit Mode */
                      <div className="space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <input
                            className={inputCls}
                            placeholder="Group name"
                            value={editingGroup.name}
                            onChange={(e) => setEditingGroup(p => ({ ...p, name: e.target.value }))}
                            data-testid="edit-group-name"
                          />
                          <input
                            className={inputCls}
                            placeholder="Description"
                            value={editingGroup.description}
                            onChange={(e) => setEditingGroup(p => ({ ...p, description: e.target.value }))}
                            data-testid="edit-group-desc"
                          />
                          <select
                            className={inputCls}
                            value={editingGroup.parent_id || ''}
                            onChange={(e) => setEditingGroup(p => ({ ...p, parent_id: e.target.value || null }))}
                            data-testid="edit-group-parent"
                          >
                            <option value="">No parent (top-level)</option>
                            {userGroups
                              .filter(g => g.id !== editingGroup.id)
                              .map(g => (
                                <option key={g.id} value={g.id}>{getGroupPath(g.id)}</option>
                              ))}
                          </select>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={saveEditGroup} className="px-3 py-1.5 text-xs font-bold text-white rounded" style={{ backgroundColor: '#E8520A' }} data-testid="save-edit-group">
                            Save
                          </button>
                          <button onClick={() => setEditingGroup(null)} className="px-3 py-1.5 text-xs font-bold text-gray-600 border rounded">
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <>
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              {group.depth > 0 && (
                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 uppercase">Sub</span>
                              )}
                              <p className="text-sm font-bold text-gray-900" data-testid={`group-name-${group.id}`}>{group.name}</p>
                            </div>
                            {group.parent_id && (
                              <p className="text-[10px] text-gray-400 mt-0.5">
                                in {getGroupPath(group.parent_id)}
                              </p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">{group.description || 'No description'}</p>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => setBulkMigrate({ show: true, fromGroupId: group.id, selectedUsers: [], toGroupId: '' })}
                              className="text-gray-400 hover:text-blue-600 transition-colors p-1"
                              title="Bulk migrate members"
                              data-testid={`bulk-btn-${group.id}`}
                            >
                              <ArrowRightLeft size={14} />
                            </button>
                            <button
                              onClick={() => startEditGroup(group)}
                              className="text-gray-400 hover:text-orange-600 transition-colors p-1"
                              data-testid={`edit-group-${group.id}`}
                            >
                              <Edit2 size={14} />
                            </button>
                            <button
                              onClick={() => deleteGroup(group.id)}
                              className="text-gray-400 hover:text-red-600 transition-colors p-1"
                              data-testid={`delete-group-${group.id}`}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>
                        
                        {/* Members List */}
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                            Members ({group.members?.length || 0})
                          </p>
                          <div className="space-y-1 max-h-32 overflow-y-auto mb-3">
                            {group.members?.map(userId => {
                              const user = users.find(u => u.id === userId);
                              return user ? (
                                <div key={userId} className="flex items-center justify-between text-xs bg-gray-50 rounded px-2 py-1">
                                  <span className="text-gray-700">{user.username}</span>
                                  <button
                                    onClick={() => removeMemberFromGroup(group.id, userId)}
                                    className="text-gray-400 hover:text-red-600"
                                    data-testid={`remove-member-${group.id}-${userId}`}
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              ) : null;
                            })}
                          </div>
                          
                          {/* Add Member Dropdown */}
                          <select
                            onChange={(e) => {
                              if (e.target.value) {
                                addMemberToGroup(group.id, e.target.value);
                                e.target.value = '';
                              }
                            }}
                            className="w-full px-2 py-1.5 border border-gray-200 rounded text-xs text-gray-900 focus:outline-none focus:border-orange-500"
                            data-testid={`add-member-${group.id}`}
                          >
                            <option value="">+ Add User...</option>
                            {users
                              .filter(u => !group.members?.includes(u.id))
                              .map(u => (
                                <option key={u.id} value={u.id}>{u.username}</option>
                              ))}
                          </select>
                        </div>
                      </>
                    )}
                  </div>
                ))}
                
                {userGroups.length === 0 && !showNewGroup && (
                  <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
                    <Users size={32} className="mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-gray-500">No groups yet</p>
                    <p className="text-xs text-gray-400 mt-1">Create groups to organize users with specific access</p>
                  </div>
                )}
              </div>
            </div>


            <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm">
              {/* Mobile Card Layout for Users */}
              <div className="block sm:hidden divide-y divide-gray-100" data-testid="users-mobile-cards">
                {getSortedUsers().map(u => (
                  <div key={u.id} className="px-4 py-3" data-testid={`user-card-${u.id}`}>
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: u.role === 'admin' ? '#FFF0E8' : '#f3f4f6' }}>
                        <span className="text-sm font-bold" style={{ color: u.role === 'admin' ? '#E8520A' : '#6b7280' }}>
                          {(u.username || '?').charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          {editingUser?.id === u.id ? (
                            <input className="border border-gray-200 rounded px-2 py-1 text-sm text-gray-900 bg-white w-full" value={editingUser.username} onChange={e => setEditingUser(p => ({ ...p, username: e.target.value }))} />
                          ) : (
                            <span className="text-sm font-bold text-gray-900 truncate">{u.username}</span>
                          )}
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${u.role === 'admin' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>{u.role}</span>
                          <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full flex-shrink-0 ${u.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{u.active ? 'Active' : 'Disabled'}</span>
                        </div>
                        {editingUser?.id === u.id ? (
                          <div className="space-y-2 mt-2">
                            <input className="border border-gray-200 rounded px-2 py-1 text-sm text-gray-900 bg-white w-full" value={editingUser.email} onChange={e => setEditingUser(p => ({ ...p, email: e.target.value }))} placeholder="Email" />
                            <input type="password" placeholder="New password" className="border border-gray-200 rounded px-2 py-1 text-sm text-gray-900 bg-white w-full" value={editingUser.newPassword || ''} onChange={e => setEditingUser(p => ({ ...p, newPassword: e.target.value }))} />
                            <div className="flex gap-2">
                              <button onClick={saveEditUser} className="px-3 py-1.5 text-xs font-bold text-white rounded" style={{ backgroundColor: '#E8520A' }}>Save</button>
                              <button onClick={() => setEditingUser(null)} className="px-3 py-1.5 text-xs font-bold text-gray-600 border border-gray-200 rounded">Cancel</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <p className="text-xs text-gray-500 truncate">{u.email}</p>
                            <div className="flex items-center justify-between mt-2">
                              <span className="text-[10px] text-gray-400">{u.last_login ? `Last login: ${new Date(u.last_login).toLocaleDateString()}` : 'Never logged in'}</span>
                              <div className="flex items-center gap-2">
                                <button onClick={() => setEditingUser({ ...u, newPassword: '' })} className="text-gray-400 hover:text-gray-700"><Edit2 size={14} /></button>
                                <button onClick={() => toggleUser(u)} className={u.active ? 'text-orange-400 hover:text-orange-600' : 'text-green-400 hover:text-green-600'}>
                                  {u.active ? <UserX size={14} /> : <UserCheck size={14} />}
                                </button>
                                {u.id !== user?.id && (
                                  <button onClick={() => deleteUser(u.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table */}
              <div className="hidden sm:block overflow-x-auto">
              <table className="w-full min-w-[700px]">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left text-xs font-bold uppercase tracking-widest text-gray-400 px-5 py-3">
                      <button 
                        onClick={() => handleSort('name')}
                        className="flex items-center gap-1 hover:text-gray-600"
                      >
                        User
                        {sortBy === 'name' && (
                          <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                    <th className="text-left text-xs font-bold uppercase tracking-widest text-gray-400 px-5 py-3">Email</th>
                    <th className="text-left text-xs font-bold uppercase tracking-widest text-gray-400 px-5 py-3">Password</th>
                    <th className="text-left text-xs font-bold uppercase tracking-widest text-gray-400 px-5 py-3">Role</th>
                    <th className="text-left text-xs font-bold uppercase tracking-widest text-gray-400 px-5 py-3">Status</th>
                    <th className="text-left text-xs font-bold uppercase tracking-widest text-gray-400 px-5 py-3">Last Login</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {getSortedUsers().map(u => (
                    <tr key={u.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3.5">
                        {editingUser?.id === u.id ? (
                          <input className="border border-gray-200 rounded px-2 py-1 text-sm text-gray-900 bg-white w-32" value={editingUser.username} onChange={e => setEditingUser(p => ({ ...p, username: e.target.value }))} />
                        ) : (
                          <span className="text-sm font-semibold text-gray-900">{u.username}</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        {editingUser?.id === u.id ? (
                          <input className="border border-gray-200 rounded px-2 py-1 text-sm text-gray-900 bg-white w-40" value={editingUser.email} onChange={e => setEditingUser(p => ({ ...p, email: e.target.value }))} />
                        ) : (
                          <span className="text-sm text-gray-500">{u.email}</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        {editingUser?.id === u.id ? (
                          <input type="password" placeholder="New password" className="border border-gray-200 rounded px-2 py-1 text-sm text-gray-900 bg-white w-32" value={editingUser.newPassword || ''} onChange={e => setEditingUser(p => ({ ...p, newPassword: e.target.value }))} />
                        ) : (
                          <span className="text-xs text-gray-300">••••••••</span>
                        )}
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${u.role === 'admin' ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'}`}>{u.role}</span>
                      </td>
                      <td className="px-5 py-3.5">
                        <span className={`text-xs font-bold px-2 py-1 rounded-full ${u.active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{u.active ? 'Active' : 'Disabled'}</span>
                      </td>
                      <td className="px-5 py-3.5 text-xs text-gray-400">{u.last_login ? new Date(u.last_login).toLocaleDateString() : '—'}</td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2">
                          {editingUser?.id === u.id ? (
                            <>
                              <button onClick={saveEditUser} className="text-green-600 hover:text-green-800"><Check size={15} /></button>
                              <button onClick={() => setEditingUser(null)} className="text-gray-400 hover:text-gray-600"><X size={15} /></button>
                            </>
                          ) : (
                            <>
                      <button onClick={() => setEditingUser({ ...u, newPassword: '' })} className="text-gray-400 hover:text-gray-700"><Edit2 size={14} /></button>
                      <button onClick={() => toggleUser(u)} className={u.active ? 'text-orange-400 hover:text-orange-600' : 'text-green-400 hover:text-green-600'}>
                        {u.active ? <UserX size={14} /> : <UserCheck size={14} />}
                      </button>
                      {u.id !== user?.id && (
                        <button onClick={() => deleteUser(u.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                      )}
                    </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        )}


        {/* ── GALLERY MANAGEMENT TAB ── */}
        {tab === 'gallery' && (
          <div className="p-4 sm:p-8">
            <GalleryManagementTab authHeaders={authHeaders} flash={flash} />
          </div>
        )}

        {/* ── FILES & RESOURCES TAB ── */}
        {tab === 'files' && (
          <div className="p-4 sm:p-8">
            <h2 className="text-xl sm:text-2xl font-black text-gray-900">Files & Resources Management</h2>
            <p className="text-sm text-gray-500 mt-1 mb-4 sm:mb-6">Manage folders, resources, and file uploads</p>
            
            {/* Sub-navigation */}
            <div className="flex flex-wrap gap-2 mb-4 sm:mb-6 border-b border-gray-200 pb-2">
              {['folders', 'resources', 'upload'].map(subTab => (
                <button
                  key={subTab}
                  onClick={() => setFilesSubTab(subTab)}
                  className="px-4 py-2 text-sm font-bold uppercase tracking-wider rounded-lg transition-all"
                  style={{
                    backgroundColor: filesSubTab === subTab ? '#E8520A' : '#fff',
                    color: filesSubTab === subTab ? '#fff' : '#666',
                    border: filesSubTab === subTab ? '1px solid #E8520A' : '1px solid #e5e7eb'
                  }}
                >
                  {subTab === 'folders' ? 'Folders' : subTab === 'resources' ? 'Resources' : 'Upload File'}
                </button>
              ))}
            </div>
            
            {/* ── Folders Sub-Tab ── */}
            {filesSubTab === 'folders' && (
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
              <h2 className="text-lg font-black text-gray-900">Folders <span className="text-gray-400 font-normal text-sm">({folders.length})</span></h2>
              <button onClick={() => setShowNewFolder(true)} className="inline-flex items-center gap-2 px-5 py-2 font-bold text-white rounded-full text-sm self-start sm:self-auto" style={{ backgroundColor: '#E8520A' }}>
                <Plus size={14} /> NEW FOLDER
              </button>
            </div>

            {/* New Folder Form */}
            {showNewFolder && (
              <div className="bg-white rounded-2xl border border-orange-200 p-4 sm:p-6 mb-6 shadow-sm">
                <h3 className="font-black text-gray-900 mb-4 text-sm uppercase tracking-widest">New Folder</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                  <input className={inputCls} placeholder="Folder name *" value={newFolder.name} onChange={e => setNewFolder(p => ({ ...p, name: e.target.value }))} />
                  <input className={inputCls} placeholder="Description" value={newFolder.description} onChange={e => setNewFolder(p => ({ ...p, description: e.target.value }))} />
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Appearance</label>
                  <FolderVisualPicker
                    data={newFolder}
                    onChange={v => setNewFolder(p => ({ ...p, ...v }))}
                    onCoverUpload={() => {}}
                    folderId={null}
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Access Level</label>
                  <AccessSelector
                    value={newFolder.access_level}
                    allowedUsers={newFolder.allowed_users}
                    onChange={v => setNewFolder(p => ({ ...p, access_level: v, allowed_users: [] }))}
                    onUsersChange={v => setNewFolder(p => ({ ...p, allowed_users: v }))}
                    allUsers={users}
                  />
                </div>
                <div className="flex gap-2">
                  <button onClick={createFolder} className="px-5 py-2 font-bold text-white rounded-full text-sm" style={{ backgroundColor: '#E8520A' }}>CREATE</button>
                  <button onClick={() => setShowNewFolder(false)} className="px-5 py-2 text-gray-600 rounded-full text-sm border border-gray-200">CANCEL</button>
                </div>
              </div>
            )}

            {/* Folders list */}
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {folders.map(f => (
                <div key={f.id} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                  {editingFolder?.id === f.id ? (
                    <div className="p-5 space-y-3">
                      <input className={inputCls} placeholder="Folder name" value={editingFolder.name} onChange={e => setEditingFolder(p => ({ ...p, name: e.target.value }))} />
                      <input className={inputCls} placeholder="Description" value={editingFolder.description} onChange={e => setEditingFolder(p => ({ ...p, description: e.target.value }))} />
                      <FolderVisualPicker
                        data={editingFolder}
                        onChange={v => setEditingFolder(p => ({ ...p, ...v }))}
                        onCoverUpload={(file) => uploadFolderCover(f.id, file)}
                        folderId={f.id}
                      />
                      <AccessSelector
                        value={editingFolder.access_level}
                        allowedUsers={editingFolder.allowed_users || []}
                        onChange={v => setEditingFolder(p => ({ ...p, access_level: v, allowed_users: [] }))}
                        onUsersChange={v => setEditingFolder(p => ({ ...p, allowed_users: v }))}
                        allUsers={users}
                      />
                      <div className="flex gap-2">
                        <button onClick={saveEditFolder} className="px-4 py-1.5 font-bold text-white rounded-full text-xs" style={{ backgroundColor: '#E8520A' }}>SAVE</button>
                        <button onClick={() => setEditingFolder(null)} className="px-4 py-1.5 text-gray-500 rounded-full text-xs border border-gray-200">CANCEL</button>
                      </div>
                    </div>
                  ) : (
                    <div className="p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <FolderVisual folder={f} size={40} />
                          <div>
                            <p className="font-black text-gray-900 text-sm">{f.name}</p>
                            {f.description && <p className="text-xs text-gray-400 mt-0.5">{f.description}</p>}
                          </div>
                        </div>
                        <div className="flex gap-1.5 flex-shrink-0">
                          <button onClick={() => setEditingFolder({ ...f })} className="text-gray-400 hover:text-gray-700"><Edit2 size={13} /></button>
                          <button onClick={() => deleteFolder(f.id)} className="text-red-400 hover:text-red-600"><Trash2 size={13} /></button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${f.access_level === 'admin' ? 'bg-gray-700 text-gray-400' : f.access_level === 'specific' ? 'bg-gray-700 text-gray-400' : 'bg-green-100 text-green-600'}`}>
                          {f.access_level === 'admin' ? '🔐 Admin' : f.access_level === 'specific' ? `👥 ${f.allowed_users?.length || 0} user(s)` : '🌐 General'}
                        </span>
                        <span className="text-xs text-gray-400">{resources.filter(r => r.folder_id === f.id).length} resources</span>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {folders.length === 0 && !showNewFolder && (
                <div className="col-span-3 text-center py-12 text-gray-400 text-sm">No folders yet. Create one to organise your resources.</div>
              )}
            </div>
          </div>
        )}



            {/* ── Resources Sub-Tab ── */}
            {filesSubTab === 'resources' && (
          <div>
            {/* Quick Add Link */}
            <QuickAddLink
              folders={folders}
              users={users}
              onAdd={async (res) => {
                try {
                  await axios.post(`${BACKEND_URL}/api/portal/resources`, res, { headers: authHeaders() });
                  flash('Link added'); loadResources();
                } catch (e) { flash('Error adding link', true); }
              }}
            />

            <div className="flex items-center justify-between mb-5 mt-8">
              <h2 className="text-lg font-black text-gray-900">All Resources <span className="text-gray-400 font-normal text-sm">({resources.length})</span></h2>
              <button onClick={() => setShowNewRes(true)} className="inline-flex items-center gap-2 px-5 py-2 font-bold text-white rounded-full text-sm" style={{ backgroundColor: '#E8520A' }}>
                <Plus size={14} /> ADD RESOURCE
              </button>
            </div>

            {showNewRes && (
              <div className="bg-white rounded-2xl border border-orange-200 p-6 mb-5 shadow-sm">
                <h3 className="font-black text-gray-900 mb-4 text-sm uppercase tracking-widest">New Resource</h3>
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <input className={inputCls + ' col-span-2'} placeholder="Title *" value={newRes.title} onChange={e => setNewRes(p => ({ ...p, title: e.target.value }))} />
                  <input className={inputCls + ' col-span-2'} placeholder="Description" value={newRes.description} onChange={e => setNewRes(p => ({ ...p, description: e.target.value }))} />
                  <select className={inputCls + ' bg-white'} value={newRes.category} onChange={e => setNewRes(p => ({ ...p, category: e.target.value }))}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                  <select className={inputCls + ' bg-white'} value={newRes.product} onChange={e => setNewRes(p => ({ ...p, product: e.target.value }))}>
                    {PRODUCTS.map(c => <option key={c}>{c}</option>)}
                  </select>
                  <select className={inputCls + ' bg-white col-span-2'} value={newRes.folder_id} onChange={e => setNewRes(p => ({ ...p, folder_id: e.target.value }))}>
                    <option value="">— No folder (root) —</option>
                    {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                  <input className={inputCls} placeholder="URL or file link" value={newRes.url} onChange={e => setNewRes(p => ({ ...p, url: e.target.value }))} />
                  <select className={inputCls + ' bg-white'} value={newRes.file_type} onChange={e => setNewRes(p => ({ ...p, file_type: e.target.value }))}>
                    {FILE_TYPES.map(t => <option key={t}>{t}</option>)}
                  </select>
                  <div className="col-span-2">
                    <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">Access</label>
                    <AccessSelector
                      value={newRes.access_level}
                      allowedUsers={newRes.allowed_users}
                      onChange={v => setNewRes(p => ({ ...p, access_level: v, allowed_users: [] }))}
                      onUsersChange={v => setNewRes(p => ({ ...p, allowed_users: v }))}
                      allUsers={users}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <button onClick={createResource} className="px-5 py-2 font-bold text-white rounded-full text-sm" style={{ backgroundColor: '#E8520A' }}>ADD</button>
                  <button onClick={() => setShowNewRes(false)} className="px-5 py-2 font-bold text-gray-600 rounded-full text-sm border border-gray-200">CANCEL</button>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {resources.map(res => (
                <div key={res.id} className="bg-white rounded-xl border border-gray-100 px-5 py-4 shadow-sm">
                  {editingRes?.id === res.id ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <input className={inputCls + ' col-span-2'} value={editingRes.title} onChange={e => setEditingRes(p => ({ ...p, title: e.target.value }))} />
                      <input className={inputCls + ' col-span-2'} value={editingRes.description || ''} onChange={e => setEditingRes(p => ({ ...p, description: e.target.value }))} />
                      <select className={inputCls + ' bg-white'} value={editingRes.category} onChange={e => setEditingRes(p => ({ ...p, category: e.target.value }))}>
                        {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                      </select>
                      <select className={inputCls + ' bg-white'} value={editingRes.product} onChange={e => setEditingRes(p => ({ ...p, product: e.target.value }))}>
                        {PRODUCTS.map(c => <option key={c}>{c}</option>)}
                      </select>
                      <select className={inputCls + ' bg-white col-span-2'} value={editingRes.folder_id || ''} onChange={e => setEditingRes(p => ({ ...p, folder_id: e.target.value }))}>
                        <option value="">— No folder (root) —</option>
                        {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                      </select>
                      {!editingRes.file_id && (
                        <input className={inputCls} placeholder="URL" value={editingRes.url || ''} onChange={e => setEditingRes(p => ({ ...p, url: e.target.value }))} />
                      )}
                      <select className={inputCls + ' bg-white'} value={editingRes.file_type} onChange={e => setEditingRes(p => ({ ...p, file_type: e.target.value }))}>
                        {FILE_TYPES.map(t => <option key={t}>{t}</option>)}
                      </select>
                      <div className="col-span-2 mt-1">
                        <p className="text-xs font-bold uppercase tracking-widest text-gray-400 mb-2">Access Level</p>
                        <AccessSelector
                          value={editingRes.access_level || 'general'}
                          allowedUsers={editingRes.allowed_users || []}
                          onChange={v => setEditingRes(p => ({ ...p, access_level: v, allowed_users: [] }))}
                          onUsersChange={v => setEditingRes(p => ({ ...p, allowed_users: v }))}
                          allUsers={users}
                        />
                      </div>
                      <div className="flex gap-2 col-span-2 mt-1">
                        <button onClick={saveEditRes} className="px-4 py-1.5 font-bold text-white rounded-full text-xs" style={{ backgroundColor: '#E8520A' }}>SAVE</button>
                        <button onClick={() => setEditingRes(null)} className="px-4 py-1.5 text-gray-500 rounded-full text-xs border border-gray-200">CANCEL</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          {res.folder_id && folders.find(f => f.id === res.folder_id) && (
                            <Folder size={12} style={{ color: folders.find(f => f.id === res.folder_id)?.color || '#E8520A', flexShrink: 0 }} />
                          )}
                          <p className="font-semibold text-sm text-gray-900 truncate">{res.title}</p>
                        </div>
                        <div className="flex items-center flex-wrap gap-1.5 mt-0.5">
                          {res.folder_id && folders.find(f => f.id === res.folder_id) && (
                            <span className="text-xs text-gray-400 italic">{folders.find(f => f.id === res.folder_id)?.name}</span>
                          )}
                          <span className="text-xs text-gray-400">{res.category}</span>
                          <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-500">{res.file_type}</span>
                          {res.file_id && <span className="text-xs px-1.5 py-0.5 rounded-full bg-gray-700 text-gray-400">uploaded</span>}
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${res.access_level === 'admin' ? 'bg-gray-700 text-gray-400' : res.access_level === 'specific' ? 'bg-gray-700 text-gray-400' : 'bg-green-100 text-green-600'}`}>
                            {res.access_level === 'admin' ? '🔐 Admin' : res.access_level === 'specific' ? `👥 ${res.allowed_users?.length || 0} user(s)` : '🌐 General'}
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        <button onClick={() => setEditingRes({ ...res })} className="text-gray-400 hover:text-gray-700"><Edit2 size={14} /></button>
                        <button onClick={() => deleteResource(res.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              {resources.length === 0 && (
                <div className="text-center py-12 text-gray-400 text-sm">No resources yet. Add your first one above.</div>
              )}
            </div>
          </div>
        )}


            {/* ── Upload Sub-Tab ── */}
            {filesSubTab === 'upload' && (
          <div className="max-w-2xl">
            <h2 className="text-lg font-black text-gray-900 mb-6">Upload File to Library</h2>

            {/* Drop zone */}
            <div
              className={`border-2 border-dashed rounded-2xl p-6 sm:p-10 text-center mb-6 cursor-pointer transition-all ${dragOver ? 'border-orange-400 bg-orange-50' : 'border-gray-200 hover:border-orange-300 bg-gray-50'}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.jpg,.jpeg,.png,.gif,.webp,.mp4,.mov,.avi"
                onChange={(e) => {
                  const f = e.target.files[0];
                  if (f) { setUploadFile(f); if (!uploadMeta.title) setUploadMeta(p => ({ ...p, title: f.name.replace(/\.[^.]+$/, '') })); }
                }}
              />
              {uploadFile ? (
                <div>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: '#FFF0E8' }}>
                    <CloudUpload size={22} style={{ color: '#E8520A' }} />
                  </div>
                  <p className="font-bold text-gray-900 text-sm">{uploadFile.name}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatSize(uploadFile.size)}</p>
                  <button
                    onClick={(e) => { e.stopPropagation(); setUploadFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                    className="text-xs text-red-400 hover:text-red-600 mt-2"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 bg-gray-100">
                    <Upload size={22} className="text-gray-400" />
                  </div>
                  <p className="text-gray-600 text-sm font-medium">Drag & drop or click to select a file</p>
                  <p className="text-xs text-gray-400 mt-1">PDF, DOC, XLS, PPT, Images, Videos, ZIP — max 50 MB</p>
                </div>
              )}
            </div>

            {/* Metadata */}
            <div className="space-y-3 mb-6">
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">Title <span style={{ color: '#E8520A' }}>*</span></label>
                <input className={inputCls} placeholder="Resource title" value={uploadMeta.title} onChange={e => setUploadMeta(p => ({ ...p, title: e.target.value }))} />
              </div>
              <div>
                <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">Description</label>
                <input className={inputCls} placeholder="Brief description of this file" value={uploadMeta.description} onChange={e => setUploadMeta(p => ({ ...p, description: e.target.value }))} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">Category</label>
                  <select className={inputCls + ' bg-white'} value={uploadMeta.category} onChange={e => setUploadMeta(p => ({ ...p, category: e.target.value }))}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">Product</label>
                  <select className={inputCls + ' bg-white'} value={uploadMeta.product} onChange={e => setUploadMeta(p => ({ ...p, product: e.target.value }))}>
                    {PRODUCTS.map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">Folder</label>
                  <select className={inputCls + ' bg-white'} value={uploadMeta.folder_id || ''} onChange={e => setUploadMeta(p => ({ ...p, folder_id: e.target.value }))}>
                    <option value="">— No folder (root) —</option>
                    {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
              </div>
            </div>

              <div>
                  <label className="block text-xs font-bold uppercase tracking-widest text-gray-400 mb-1.5">Access Level</label>
                  <AccessSelector
                    value={uploadMeta.access_level}
                    allowedUsers={uploadMeta.allowed_users}
                    onChange={v => setUploadMeta(p => ({ ...p, access_level: v, allowed_users: [] }))}
                    onUsersChange={v => setUploadMeta(p => ({ ...p, allowed_users: v }))}
                    allUsers={users}
                  />
                </div>
            {uploading && (
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-500 mb-1">
                  <span>Uploading…</span><span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-2">
                  <div className="h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%`, backgroundColor: '#E8520A' }} />
                </div>
              </div>
            )}

            <button
              onClick={handleUpload}
              disabled={uploading || !uploadFile}
              className="w-full py-3 font-bold text-white rounded-full text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              style={{ backgroundColor: '#E8520A' }}
            >
              {uploading ? `UPLOADING… ${uploadProgress}%` : 'UPLOAD TO LIBRARY'}
            </button>

            {/* Uploaded files list */}
            {resources.filter(r => r.file_id).length > 0 && (
              <div className="mt-10">
                <h3 className="text-sm font-black text-gray-700 uppercase tracking-widest mb-4">Uploaded Files</h3>
                <div className="space-y-2">
                  {resources.filter(r => r.file_id).map(res => {
                    const Icon = FILE_ICONS[res.file_type] || File;
                    return (
                      <div key={res.id} className="flex items-center gap-4 bg-white rounded-xl border border-gray-100 px-5 py-3.5 shadow-sm">
                        <Icon size={16} className={FILE_COLORS[res.file_type] || 'text-gray-400'} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">{res.title}</p>
                          <p className="text-xs text-gray-400">{res.original_filename} {res.file_size ? `· ${formatSize(res.file_size)}` : ''} · {res.category}</p>
                        </div>
                        <a href={`${BACKEND_URL}${res.url}?token=${user?.token}`} className="text-xs font-semibold text-gray-400 hover:text-gray-400 flex-shrink-0">Download</a>
                        <button onClick={() => deleteResource(res.id)} className="text-red-400 hover:text-red-600 flex-shrink-0"><Trash2 size={14} /></button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
          </div>
        )}

        {/* ── CHAT HISTORY TAB ── */}
        {tab === 'chats' && (
          <div className="p-4 sm:p-8">
            {/* Header with Export/Share and Delete Buttons */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <div>
                <h2 className="text-xl sm:text-2xl font-black text-gray-900">DISCO Chat History</h2>
                <p className="text-sm text-gray-500">
                  {chatSessions.length} customer {chatSessions.length === 1 ? 'conversation' : 'conversations'}
                  {selectedChats.length > 0 && ` · ${selectedChats.length} selected`}
                </p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <button
                  onClick={async () => { setChatRefreshing(true); await Promise.all([loadChatHistory(), loadCustomers()]); setChatRefreshing(false); }}
                  className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                  title="Refresh chat history"
                  data-testid="refresh-chats-btn"
                >
                  <RefreshCw size={16} className={`text-gray-500 ${chatRefreshing ? 'animate-spin' : ''}`} />
                </button>
                {selectedChats.length > 0 && (
                  <button
                    onClick={handleDeleteSelectedChats}
                    className="inline-flex items-center gap-2 px-4 py-2.5 font-bold text-red-600 border border-red-200 rounded-xl text-xs transition-all hover:bg-red-50"
                    data-testid="delete-selected-chats-btn"
                  >
                    <Trash2 size={13} /> Delete ({selectedChats.length})
                  </button>
                )}
                <ShareExportMenu
                  onExportCSV={handleExportCSV}
                  onExportPDF={exportChatPDF}
                  shareTitle="Total Composites — DISCO Chat History"
                  selectedCount={selectedChats.length}
                />
              </div>
            </div>

            <p className="text-xs text-gray-400 mb-4" data-testid="chats-last-updated">
              Last updated: {chatLastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>

            {/* Sub-navigation: Conversations | Customers */}
            <div className="flex gap-2 mb-6 border-b border-gray-200 pb-2">
              <button
                onClick={() => setChatSubTab('conversations')}
                className="px-4 py-2 text-sm font-bold uppercase tracking-wider rounded-lg transition-all"
                style={{
                  backgroundColor: chatSubTab === 'conversations' ? '#E8520A' : '#fff',
                  color: chatSubTab === 'conversations' ? '#fff' : '#666',
                  border: chatSubTab === 'conversations' ? '1px solid #E8520A' : '1px solid #e5e7eb'
                }}
              >
                Conversations
              </button>
              <button
                onClick={() => setChatSubTab('customers')}
                className="px-4 py-2 text-sm font-bold uppercase tracking-wider rounded-lg transition-all"
                style={{
                  backgroundColor: chatSubTab === 'customers' ? '#E8520A' : '#fff',
                  color: chatSubTab === 'customers' ? '#fff' : '#666',
                  border: chatSubTab === 'customers' ? '1px solid #E8520A' : '1px solid #e5e7eb'
                }}
              >
                Customers ({customers.length})
              </button>
            </div>

            {/* Conversations View */}
            {chatSubTab === 'conversations' && (
              <>
            {/* Search */}
            <div className="mb-6">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={chatSearch}
                  onChange={(e) => setChatSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-orange-500"
                />
              </div>
            </div>

            {/* Chat Sessions — Card on mobile, Table on desktop */}
            <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              {/* Mobile Card Layout */}
              <div className="block sm:hidden" data-testid="chat-mobile-cards">
                {/* Select All on mobile */}
                <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
                  <input 
                    type="checkbox"
                    checked={selectedChats.length > 0 && selectedChats.length === chatSessions.filter(s => 
                      !chatSearch || 
                      s.name?.toLowerCase().includes(chatSearch.toLowerCase()) ||
                      s.email?.toLowerCase().includes(chatSearch.toLowerCase())
                    ).length}
                    onChange={toggleAllChats}
                    className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                    style={{ accentColor: '#E8520A' }}
                  />
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                    {selectedChats.length > 0 ? `${selectedChats.length} selected` : 'Select all'}
                  </span>
                </div>
                <div className="divide-y divide-gray-100">
                  {getSortedChats()
                    .filter(session => 
                      !chatSearch || 
                      session.name?.toLowerCase().includes(chatSearch.toLowerCase()) ||
                      session.email?.toLowerCase().includes(chatSearch.toLowerCase())
                    )
                    .map((session) => (
                      <div key={session._id} className="px-4 py-3" data-testid={`chat-card-${session._id}`}>
                        <div className="flex items-start gap-3">
                          <input 
                            type="checkbox"
                            checked={selectedChats.includes(session._id)}
                            onChange={() => toggleChatSelection(session._id)}
                            className="w-4 h-4 rounded border-gray-300 cursor-pointer mt-1 flex-shrink-0"
                            style={{ accentColor: '#E8520A' }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="text-sm font-bold text-gray-900 truncate">{session.name || 'Anonymous'}</span>
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-600 flex-shrink-0">
                                {session.message_count} msgs
                              </span>
                            </div>
                            <p className="text-xs text-gray-500 truncate mb-1">{session.email || 'N/A'}</p>
                            <p className="text-xs text-gray-400 truncate italic mb-2">{session.first_message || 'No message preview'}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] text-gray-400">
                                {session.last_updated ? new Date(session.last_updated).toLocaleDateString('en-CA', { 
                                  month: 'short', day: 'numeric', year: 'numeric'
                                }) : 'N/A'}
                              </span>
                              <button
                                onClick={() => setExpandedChat(expandedChat === session._id ? null : session._id)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold rounded-lg transition-all"
                                style={expandedChat === session._id 
                                  ? { backgroundColor: '#FFF0E8', color: '#E8520A', border: '1px solid #E8520A' }
                                  : { backgroundColor: '#f9fafb', color: '#374151', border: '1px solid #e5e7eb' }
                                }
                              >
                                {expandedChat === session._id ? <><ChevronUp size={12} /> Hide</> : <><ChevronDown size={12} /> View</>}
                              </button>
                            </div>
                          </div>
                        </div>
                        {/* Expanded conversation on mobile */}
                        {expandedChat === session._id && (
                          <div className="mt-3 bg-gray-50 rounded-xl p-3 border border-gray-100">
                            <h4 className="text-[10px] font-bold uppercase tracking-wider text-gray-400 mb-2">Conversation</h4>
                            <div className="space-y-2 max-h-72 overflow-y-auto">
                              {session.messages?.map((msg, idx) => (
                                <div key={msg.timestamp || `msg-${idx}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                  <div className={`max-w-[85%] px-3 py-2 rounded-lg ${
                                    msg.role === 'user' 
                                      ? 'bg-orange-100 border border-orange-200 text-gray-900' 
                                      : 'bg-white border border-gray-200 text-gray-700'
                                  }`}>
                                    <p className="text-[10px] font-bold uppercase mb-0.5" style={{ color: msg.role === 'user' ? '#E8520A' : '#666' }}>
                                      {msg.role === 'user' ? session.name : 'DISCO'}
                                    </p>
                                    <p className="text-xs leading-relaxed">{msg.content}</p>
                                    <p className="text-[10px] text-gray-400 mt-0.5">
                                      {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' }) : ''}
                                    </p>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              </div>

              {/* Desktop Table */}
              <div className="hidden sm:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-center w-12">
                      <input 
                        type="checkbox"
                        checked={selectedChats.length > 0 && selectedChats.length === chatSessions.filter(s => 
                          !chatSearch || 
                          s.name?.toLowerCase().includes(chatSearch.toLowerCase()) ||
                          s.email?.toLowerCase().includes(chatSearch.toLowerCase())
                        ).length}
                        onChange={toggleAllChats}
                        className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                        style={{ accentColor: '#E8520A' }}
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                      <button 
                        onClick={() => handleSort('name')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Customer
                        {sortBy === 'name' && (
                          <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Email</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">First Message</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Messages</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                      <button 
                        onClick={() => handleSort('date')}
                        className="flex items-center gap-1 hover:text-gray-700"
                      >
                        Date
                        {sortBy === 'date' && (
                          <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                        )}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">View</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {getSortedChats()
                    .filter(session => 
                      !chatSearch || 
                      session.name?.toLowerCase().includes(chatSearch.toLowerCase()) ||
                      session.email?.toLowerCase().includes(chatSearch.toLowerCase())
                    )
                    .map((session) => (
                      <React.Fragment key={session._id}>
                        <tr className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 text-center">
                            <input 
                              type="checkbox"
                              checked={selectedChats.includes(session._id)}
                              onChange={() => toggleChatSelection(session._id)}
                              className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                              style={{ accentColor: '#E8520A' }}
                            />
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900">{session.name || 'Anonymous'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">{session.email || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            <div className="max-w-xs truncate">{session.first_message || 'N/A'}</div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500 text-center">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                              {session.message_count}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {session.last_updated ? new Date(session.last_updated).toLocaleDateString('en-CA', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            }) : 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => setExpandedChat(expandedChat === session._id ? null : session._id)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-gray-700 border border-gray-300 rounded-lg hover:border-orange-500 hover:text-orange-600 transition-all"
                            >
                              {expandedChat === session._id ? (
                                <><ChevronUp size={14} /> Hide</>
                              ) : (
                                <><ChevronDown size={14} /> View</>
                              )}
                            </button>
                          </td>
                        </tr>
                        
                        {/* Expanded conversation view */}
                        {expandedChat === session._id && (
                          <tr>
                            <td colSpan="7" className="px-4 py-4 bg-gray-50">
                              <div className="max-w-4xl">
                                <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Full Conversation</h4>
                                <div className="space-y-3 max-h-96 overflow-y-auto">
                                  {session.messages?.map((msg, idx) => (
                                    <div key={msg.timestamp || `msg-${idx}`} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                                      <div className={`max-w-md px-4 py-2.5 rounded-lg ${
                                        msg.role === 'user' 
                                          ? 'bg-orange-100 border border-orange-200 text-gray-900' 
                                          : 'bg-white border border-gray-200 text-gray-700'
                                      }`}>
                                        <p className="text-xs font-bold uppercase tracking-wider mb-1" style={{ color: msg.role === 'user' ? '#E8520A' : '#666' }}>
                                          {msg.role === 'user' ? session.name : 'DISCO'}
                                        </p>
                                        <p className="text-sm leading-relaxed">{msg.content}</p>
                                        <p className="text-xs text-gray-400 mt-1">
                                          {msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' }) : ''}
                                        </p>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                </tbody>
              </table>
              </div>
              
              {/* Empty state */}
              {chatSessions.length === 0 && (
                <div className="text-center py-12">
                  <MessageSquare size={48} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-sm font-medium text-gray-500">No chat conversations yet</p>
                  <p className="text-xs text-gray-400 mt-1">Customer inquiries from DISCO chatbot will appear here</p>
                </div>
              )}
              
              {/* No search results */}
              {chatSessions.length > 0 && chatSearch && chatSessions.filter(s => 
                s.name?.toLowerCase().includes(chatSearch.toLowerCase()) ||
                s.email?.toLowerCase().includes(chatSearch.toLowerCase())
              ).length === 0 && (
                <div className="text-center py-12">
                  <Search size={48} className="mx-auto text-gray-300 mb-3" />
                  <p className="text-sm font-medium text-gray-500">No results for "{chatSearch}"</p>
                </div>
              )}
            </div>
            </>
            )}

            {/* Customers Registry View */}
            {chatSubTab === 'customers' && (
              <>
                {/* Delete button for customers */}
                {selectedCustomers.length > 0 && (
                  <div className="mb-4">
                    <button
                      onClick={handleDeleteCustomers}
                      className="inline-flex items-center gap-2 px-5 py-2.5 font-bold text-white rounded-lg text-sm transition-all hover:opacity-90"
                      style={{ backgroundColor: '#DC2626' }}
                    >
                      <Trash2 size={16} /> Delete {selectedCustomers.length} Customer(s) & All Chats
                    </button>
                  </div>
                )}
                
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                {/* Mobile Card Layout */}
                <div className="block sm:hidden" data-testid="chat-customers-mobile">
                  <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 border-b border-gray-200">
                    <input 
                      type="checkbox"
                      checked={selectedCustomers.length > 0 && selectedCustomers.length === customers.length}
                      onChange={toggleAllCustomers}
                      className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                      style={{ accentColor: '#E8520A' }}
                    />
                    <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">
                      {selectedCustomers.length > 0 ? `${selectedCustomers.length} selected` : 'Select all'}
                    </span>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {getSortedCustomers().map((customer) => (
                      <div key={customer.email} className="px-4 py-3" data-testid={`customer-card-${customer.email}`}>
                        <div className="flex items-start gap-3">
                          <input 
                            type="checkbox"
                            checked={selectedCustomers.includes(customer.email)}
                            onChange={() => toggleCustomerSelection(customer.email)}
                            className="w-4 h-4 rounded border-gray-300 cursor-pointer mt-1 flex-shrink-0"
                            style={{ accentColor: '#E8520A' }}
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <span className="text-sm font-bold text-gray-900 truncate">{customer.name}</span>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-orange-100 text-orange-700">
                                  {customer.session_count || 0} sessions
                                </span>
                                <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-gray-100 text-gray-600">
                                  {customer.total_sessions || 0} msgs
                                </span>
                              </div>
                            </div>
                            <p className="text-xs text-gray-500 truncate mb-2">{customer.email}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-[10px] text-gray-400">
                                Last: {customer.last_session ? new Date(customer.last_session).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' }) : 'N/A'}
                              </span>
                              <button
                                onClick={() => handleViewCustomerChats(customer.email, customer.name)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold text-white rounded-lg transition-all hover:opacity-90"
                                style={{ backgroundColor: '#E8520A' }}
                              >
                                View Chats
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Desktop Table */}
                <div className="hidden sm:block overflow-x-auto">
                <table className="w-full min-w-[600px]">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-4 py-3 text-center w-12">
                        <input 
                          type="checkbox"
                          checked={selectedCustomers.length > 0 && selectedCustomers.length === customers.length}
                          onChange={toggleAllCustomers}
                          className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                          style={{ accentColor: '#E8520A' }}
                        />
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                        <button 
                          onClick={() => handleSort('name')}
                          className="flex items-center gap-1 hover:text-gray-700"
                        >
                          Name
                          {sortBy === 'name' && (
                            <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">First Session</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">
                        <button 
                          onClick={() => handleSort('date')}
                          className="flex items-center gap-1 hover:text-gray-700"
                        >
                          Last Session
                          {sortBy === 'date' && (
                            <span>{sortOrder === 'asc' ? '↑' : '↓'}</span>
                          )}
                        </button>
                      </th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Sessions</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Messages</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {getSortedCustomers().map((customer) => (
                      <tr key={customer.email} className="hover:bg-gray-50 transition-colors">
                        <td className="px-4 py-3 text-center">
                          <input 
                            type="checkbox"
                            checked={selectedCustomers.includes(customer.email)}
                            onChange={() => toggleCustomerSelection(customer.email)}
                            className="w-4 h-4 rounded border-gray-300 cursor-pointer"
                            style={{ accentColor: '#E8520A' }}
                          />
                        </td>
                        <td className="px-4 py-3 text-sm font-medium text-gray-900">{customer.name}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">{customer.email}</td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {customer.first_session ? new Date(customer.first_session).toLocaleDateString('en-CA', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric'
                          }) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-500">
                          {customer.last_session ? new Date(customer.last_session).toLocaleDateString('en-CA', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric'
                          }) : 'N/A'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
                            {customer.session_count || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                            {customer.total_sessions || 0}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleViewCustomerChats(customer.email, customer.name)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-bold text-white rounded-lg transition-all hover:opacity-90"
                            style={{ backgroundColor: '#E8520A' }}
                          >
                            View Chats
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                
                {/* Empty state */}
                {customers.length === 0 && (
                  <div className="text-center py-12">
                    <Users size={48} className="mx-auto text-gray-300 mb-3" />
                    <p className="text-sm font-medium text-gray-500">No customers yet</p>
                    <p className="text-xs text-gray-400 mt-1">Customers who interact with DISCO will appear here</p>
                  </div>
                )}
              </div>
              </>
            )}
          </div>
        )}

        {/* ── ARTICLES MANAGEMENT TAB ── */}
        {tab === 'articles' && (
          <ArticlesManager token={token} />
        )}

        {/* ── EVENTS MANAGEMENT TAB ── */}
        {tab === 'events' && (
          <div className="p-4 sm:p-8">
            <EventsManagementTab authHeaders={authHeaders} flash={flash} onUnsavedChanges={setEventsUnsaved} />
          </div>
        )}

        {/* ── FAQ MANAGEMENT TAB ── */}
        {tab === 'faq' && (
          <div className="p-4 sm:p-8">
            <FAQManagementTab authHeaders={authHeaders} flash={flash} onUnsavedChanges={setFaqUnsaved} />
          </div>
        )}

        {/* ── WEB ANALYTICS TAB ── */}
        {tab === 'analytics' && (
          <div className="p-4 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl sm:text-2xl font-black text-gray-900">Web Analytics</h2>
                <p className="text-sm text-gray-500 mt-1">Website traffic, search keywords, and visitor insights</p>
              </div>
              <button
                onClick={() => { setAnalyticsRefreshing(true); setAnalyticsKey(k => k + 1); setAnalyticsLastUpdated(new Date()); setTimeout(() => setAnalyticsRefreshing(false), 1500); }}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 transition-colors"
                title="Refresh analytics"
                data-testid="refresh-analytics-btn"
              >
                <RefreshCw size={16} className={`text-gray-500 ${analyticsRefreshing ? 'animate-spin' : ''}`} />
              </button>
            </div>
            <p className="text-xs text-gray-400 mb-4" data-testid="analytics-last-updated">
              Last updated: {analyticsLastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>

            {/* Weekly Reports Section */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 mb-6">
              <WeeklyReportsSection key={`reports-${analyticsKey}`} authHeaders={authHeaders()} />
            </div>

            {/* Visitor Map */}
            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 mb-6">
              <VisitorMapWidget key={`map-${analyticsKey}`} authHeaders={authHeaders()} />
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6">
              <AnalyticsWidget key={`ga-${analyticsKey}`} />
            </div>
          </div>
        )}

          </div>{/* end main content */}
        </div>{/* end flex */}
      </div>
    </div>
  );
};

export default AdminPanel;
